// src/pages/projects/ContractsPage.jsx
// Contracts management — Mumbai/Rotterdam consultancy offers & appointment letters
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Plus, FileText, Eye, Download, Trash2, ArrowLeft, Save, CheckCircle2, AlertCircle, Circle, Pencil, Copy, GripVertical } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  BrioPageHeader,
  BrioFrame,
  BrioCard,
  BrioButton,
  BrioBadge,
  BrioSelect,
  BrioInput,
  BrioModal,
  BrioAlert,
  BrioTable,
  BrioActionGroup,
  BrioAnalyticsSurface,
} from '../../components/BrioComponents';
import { useSupabaseContracts } from '../../hooks/useSupabaseContracts';
import { useSupabaseInquiries } from '../../hooks/useSupabase';
import { buildContractHtml, DEFAULT_FEE_NOTES } from '../../contracts/renderContractHtml';
import { buildContractHtmlNL } from '../../contracts/renderContractHtmlNL';
import { buildStandardTcHtml, getStandardTcBodyHtml } from '../../contracts/renderStandardTcHtml';
import { buildScopeHtml } from '../../scopes/renderScopeHtml';
import { supabase } from '../../supabaseClient';
import { resolveBrandingImageSrc } from '../../utils/brandingImages';

const PMC_PARENT = 'Project Management Consultancy';
const PMC_SUBITEMS = [
  'Issue of Interim Certificate to the contractor(s) after checking the bills',
  'Taking final measurements and issuing final certificates after completion of works',
  'Bill of Quantities preparation, tender documents, Bill Checking',
  'Project and Time Management, cost management and day to day supervision',
];

const STANDARD_SERVICES = [
  'Topographical survey',
  'Structural Design',
  'Specialised engineering design for alternative structural systems and raw materials',
  'Landscape Design',
  'Sustainability consultancy',
  'Pool consultancy',
  'Horticulture implementation',
  'All Services including HVAC, Electrical & Mechanical Consulting, Fire Safety, Plumbing, Sewerage and sanitary consulting, LEED certification',
  'Lighting Design',
  'Hospitality / Back of House Consultancy',
  'Shop drawings (for fabricated items, loose furniture, prefabricated steel construction, doors and windows)',
  'Sourcing and Importing of Furniture and Accessories (Administration, Quotations, Payments, Coordination)',
  'All statutory permissions, sanctions and approvals, including the Final Occupancy Certificate',
  PMC_PARENT,
];

const STAGE_TEMPLATES = {
  mumbai: [
    { stageName: 'Stage 1: Concept Design', proportion: '10%', cumulative: '10%' },
    { stageName: 'Stage 2: Schematic Design', proportion: '15%', cumulative: '25%' },
    { stageName: 'Stage 3: Design Development', proportion: '15%', cumulative: '40%' },
    { stageName: 'Stage 4: Technical Design (for Approval)', proportion: '10%', cumulative: '50%' },
    { stageName: 'Stage 5: Working Drawings (Construction Documents)', proportion: '15%', cumulative: '65%' },
    { stageName: 'Stage 6: Interior Design Development', proportion: '10%', cumulative: '75%' },
    { stageName: 'Stage 7: Interior Working Drawings', proportion: '10%', cumulative: '85%' },
    { stageName: 'Stage 8: Tender / Contractor Selection', proportion: '5%', cumulative: '90%' },
    { stageName: 'Stage 9: Construction Administration', proportion: '5%', cumulative: '95%' },
    { stageName: 'Stage 10: Project Completion', proportion: '5%', cumulative: '100%' },
  ],
  rotterdam: [
    { stageName: 'Stage 1: Schematic Design (SO)', proportion: '33%', cumulative: '33%' },
    { stageName: 'Stage 2: Design Development (VO)', proportion: '34%', cumulative: '67%' },
    { stageName: 'Stage 3: Technical Design & Construction Documents (TO/UO)', proportion: '33%', cumulative: '100%' },
  ],
};

const STATUS_VARIANT = {
  draft: 'neutral',
  sent: 'pending',
  confirmed: 'success',
  appointment_letter: 'info',
};

const OFFICE_LABEL = { mumbai: 'IN', rotterdam: 'NL' };

function formatDate(d) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return String(d);
  }
}

// BRIO_YYYYMMDD-ProjectName-in-Location-Type-Status-Version.pdf
function makePdfFilename({ projectName, location, type, status, version, date } = {}) {
  const slugify = (s) => (s || '').replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '').substring(0, 40);
  const d = date || new Date();
  const dateStr = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
  const proj = slugify(projectName);
  const loc = slugify(location);
  const ver = (version || '').replace(/\./g, '_').replace(/[^a-zA-Z0-9_]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '') || 'V1';
  const parts = ['BRIO', dateStr, proj, 'in', loc, type, status, ver].filter(Boolean);
  return parts.join('-') + '.pdf';
}

function ContractsPage({ currentUser, isAdmin, settingsHook, scopeTemplatesHook, contractScopesHook, activeTasks, onNavigate }) {
  // contract_scopes and scope_templates are left out of the initial settings
  // fetch because of their size — pull them now that a page needs them.
  const ensureScopes = contractScopesHook?.ensureLoaded;
  const ensureTemplates = scopeTemplatesHook?.ensureLoaded;
  useEffect(() => { ensureScopes?.(); ensureTemplates?.(); }, [ensureScopes, ensureTemplates]);

  const contractsHook = useSupabaseContracts();
  const {
    contracts, tcTemplates, loading, error,
    createContract, updateContract, duplicateContract, deleteContract,
    createTcTemplate, updateTcTemplate, deleteTcTemplate,
  } = contractsHook;

  const { inquiries: allInquiries, updateInquiry } = useSupabaseInquiries();
  const inquiries = (allInquiries || []).filter(i => i.status === 'active');

  const [selectedId, setSelectedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newInquiryId, setNewInquiryId] = useState('');
  const [newOffice, setNewOffice] = useState('mumbai');
  const [newContractName, setNewContractName] = useState('');
  const [newVersionName, setNewVersionName] = useState('V1.0');
  const [activeTab, setActiveTab] = useState('offer');
  const [previewHtml, setPreviewHtml] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [tcPreviewHtml, setTcPreviewHtml] = useState(null);
  const [tcExporting, setTcExporting] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  // Tracks the effective contractSettings so that Preview/Download always use the
  // latest UI state even before a DB round-trip confirms the save.
  const [localContractSettings, setLocalContractSettings] = useState({});
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [listQuickPdf, setListQuickPdf] = useState(null); // { html, title, filename }
  const [listQuickExporting, setListQuickExporting] = useState(false);

  const selectedContract = useMemo(
    () => contracts.find(c => c.id === selectedId) || null,
    [contracts, selectedId]
  );

  // Initialize localContractSettings only when switching to a different contract.
  // Do NOT depend on selectedContract?.contractSettings — a successful save of any
  // other field triggers a DB round-trip that returns contract_settings:{} if the
  // migration hasn't been applied, which would wipe the local state.
  React.useEffect(() => {
    setLocalContractSettings(selectedContract?.contractSettings || {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const linkedInquiry = useMemo(() => {
    if (!selectedContract) return null;
    return inquiries.find(i => i.id === selectedContract.inquiryId) || null;
  }, [selectedContract, inquiries]);

  // Scope versions associated with the current contract's project or inquiry.
  // Shown in the ScopeTab SOW template selector alongside global base templates.
  const projectScopes = useMemo(() => {
    if (!contractScopesHook) return [];
    const projId = linkedInquiry?.convertedProjectId;
    const inqId = selectedContract?.inquiryId;
    const byProject = projId ? contractScopesHook.getScopesForProject(String(projId)) : [];
    const byInquiry = inqId && contractScopesHook.getScopesForInquiry
      ? contractScopesHook.getScopesForInquiry(String(inqId)).filter(s => !projId || s.projectId == null)
      : [];
    return [...byProject, ...byInquiry].filter(s => s.status !== 'archived');
  }, [contractScopesHook, linkedInquiry, selectedContract?.inquiryId]);

  const inquiryById = useCallback(
    (id) => inquiries.find(i => i.id === id) || null,
    [inquiries]
  );

  const handleSortClick = useCallback((key) => {
    if (sortBy === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(key); setSortDir('desc'); }
  }, [sortBy]);

  const getSortIndicator = (key) => sortBy === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  const sortedContracts = useMemo(() => {
    return [...contracts].sort((a, b) => {
      const inqA = inquiryById(a.inquiryId);
      const inqB = inquiryById(b.inquiryId);
      let va, vb;
      switch (sortBy) {
        case 'project':
          va = (inqA?.projectName || '').toLowerCase();
          vb = (inqB?.projectName || '').toLowerCase();
          return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        case 'contractName':
          va = (a.contractName || '').toLowerCase();
          vb = (b.contractName || '').toLowerCase();
          return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        case 'versionName':
          va = (a.versionName || '').toLowerCase();
          vb = (b.versionName || '').toLowerCase();
          return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        case 'client':
          va = ((inqA?.clientNameOverride || inqA?.clientName) || '').toLowerCase();
          vb = ((inqB?.clientNameOverride || inqB?.clientName) || '').toLowerCase();
          return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        case 'office':
          va = a.office || '';
          vb = b.office || '';
          return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        case 'status':
          va = a.status || '';
          vb = b.status || '';
          return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        case 'docType':
          va = a.docType || '';
          vb = b.docType || '';
          return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        case 'createdAt':
        default:
          va = a.createdAt || '';
          vb = b.createdAt || '';
          if (va < vb) return sortDir === 'asc' ? -1 : 1;
          if (va > vb) return sortDir === 'asc' ? 1 : -1;
          return 0;
      }
    });
  }, [contracts, sortBy, sortDir, inquiryById]);

  const handleInquiryField = useCallback(async (field, value) => {
    if (!linkedInquiry) return;
    await updateInquiry(linkedInquiry.id, { [field]: value });
  }, [linkedInquiry, updateInquiry]);

  // ── Create ──
  const handleCreate = useCallback(async () => {
    if (!newInquiryId) return;
    const res = await createContract({
      inquiryId: newInquiryId,
      office: newOffice,
      status: 'draft',
      docType: 'offer',
      contractName: newContractName.trim(),
      versionName: newVersionName.trim(),
      excludedServices: [...STANDARD_SERVICES, ...PMC_SUBITEMS],
      constructionCoordFee: newOffice === 'mumbai' ? 150000 : null,
      signatoryName: newOffice === 'mumbai' ? 'Robert Verrijt' : '',
      signatoryTitle: 'Director',
      signatoryDin: newOffice === 'mumbai' ? 'DIN No: 03480059' : '',
      feeStageOverrides: [{ stageName: 'Advance', proportion: '', cumulative: '' }],
    });
    if (res.success) {
      setShowCreate(false);
      setNewInquiryId('');
      setNewContractName('');
      setNewVersionName('V1.0');
      setSelectedId(res.data.id);
      setActiveTab('offer');
    }
  }, [newInquiryId, newOffice, newContractName, newVersionName, createContract]);

  // ── Duplicate ──
  const handleDuplicate = useCallback(async (id) => {
    const res = await duplicateContract(id);
    if (res.success) {
      setSelectedId(res.data.id);
      setActiveTab('offer');
    }
  }, [duplicateContract]);

  // ── Assemble contractData for renderer ──
  const buildContractData = useCallback((contract, inquiry) => {
    if (!contract) return null;
    const feeOffers = inquiry?.feeOffers || [];
    const offer = contract.feeOfferIndex != null ? feeOffers[contract.feeOfferIndex] : null;
    const areaStatements = inquiry?.areaStatements || [];
    // Find the area statement version whose group IDs match the offer's groupRates keys.
    // Each version re-generates IDs when forked, so we must match by ID set, not by version index.
    const offerGroupIdSet = offer?.groupRates ? new Set(Object.keys(offer.groupRates)) : new Set();
    let areaVersion = null;
    if (offerGroupIdSet.size > 0) {
      areaVersion = areaStatements.find(a => (a.groups || []).some(g => offerGroupIdSet.has(g.id)));
    }
    if (!areaVersion) {
      areaVersion = areaStatements.find(a => String(a.id) === String(contract.areaStatementVersionId))
        || (areaStatements.length > 0 ? areaStatements[areaStatements.length - 1] : null);
    }

    // Compute constructed area per group (indoor + covered_outdoor + non_covered_outdoor + walls)
    const computeGroupConstructedArea = (g) => {
      const rows = g.subrows || g.rows || [];
      const indoorCarpet = rows.reduce((s, r) => s + (parseFloat(r.indoor) || 0), 0);
      const totalCarpet = rows.reduce((s, r) =>
        s + (parseFloat(r.indoor) || 0) + (parseFloat(r.covered_outdoor) || 0) + (parseFloat(r.non_covered_outdoor) || 0), 0);
      const wallsPct = parseFloat(g.wallsCirculationPct) || 0;
      return Math.round(totalCarpet + indoorCarpet * (wallsPct / 100));
    };

    const SQM_FACTOR = 10.764;
    const areaUnit = contract.contractSettings?.areaUnit || 'sqft';
    const rawGroups = areaVersion?.groups || [];
    // areaGroups.constructedArea is in the chosen unit (sqm or sqft) for display.
    // The raw sqft value is kept separately for fee calculation against offer.groupRates
    // which are stored per-sqft from the inquiry fee offers page.
    const areaGroups = rawGroups.map(g => ({
      id: g.id || '',
      code: g.code || '',
      label: g.label || g.name || '',
      wallsCirculationPct: parseFloat(g.wallsCirculationPct) || 0,
      constructedArea: areaUnit === 'sqm'
        ? Math.round(computeGroupConstructedArea(g) / SQM_FACTOR)
        : computeGroupConstructedArea(g),
      constructedAreaSqft: computeGroupConstructedArea(g), // always sqft, for fee calc with offer.groupRates
      subrows: (g.subrows || g.rows || []).map(r => ({
        num: r.num ?? '',
        label: r.label ?? '',
        indoor: r.indoor ?? '',
        covered_outdoor: r.covered_outdoor ?? r.coveredOutdoor ?? '',
        non_covered_outdoor: r.non_covered_outdoor ?? r.nonCoveredOutdoor ?? '',
      })),
    }));
    const totalConstructedArea = areaGroups.reduce((s, g) => s + (Number(g.constructedArea) || 0), 0) || '';
    // Helper: offer.groupRates are stored per-sqft; convert rate to chosen unit for display.
    const cvOfferRate = (r) => areaUnit === 'sqm' ? Math.round(r * SQM_FACTOR) : r;

    // Build fee rows from groupRates (new model) or fall back to legacy lines/groups
    let feeOfferGroups = [];
    let totalDesignFee = 0;
    if (offer?.groupRates && Object.keys(offer.groupRates).length > 0) {
      const feeLineGroupings = (contract.contractSettings?.feeLineGroupings || []).filter(gl => gl.groupIds?.length > 0);
      if (feeLineGroupings.length > 0) {
        // Custom groupings: rate entered by user in chosen unit on contracts page — no conversion.
        const usedGroupIds = new Set(feeLineGroupings.flatMap(gl => gl.groupIds));
        // One row per custom grouping
        const groupingRows = feeLineGroupings.map(gl => {
          const memberGroups = areaGroups.filter(g => gl.groupIds.includes(g.id));
          const area = memberGroups.reduce((s, g) => s + (Number(g.constructedArea) || 0), 0);
          const rate = parseFloat(gl.rate) || 0;
          const label = gl.label || memberGroups.map(g => g.label).join(' + ');
          return { label, constructedArea: area, rate, fee: Math.round(rate * area) };
        });
        // Remaining groups use offer.groupRates (per-sqft); convert rate and recalculate fee.
        const remainingRows = areaGroups
          .filter(g => !usedGroupIds.has(g.id))
          .map(g => {
            const rateSqft = parseFloat(offer.groupRates[g.id]) || 0;
            const rate = cvOfferRate(rateSqft);
            const area = Number(g.constructedArea) || 0;
            const fee = Math.round(rateSqft * (g.constructedAreaSqft || 0));
            return { label: g.label, constructedArea: area, rate, fee };
          })
          .filter(g => g.rate > 0 || g.constructedArea > 0);
        feeOfferGroups = [...groupingRows, ...remainingRows];
      } else {
        // No custom groupings — use offer.groupRates directly (stored per-sqft).
        // Convert rate to chosen unit for display; compute fee against raw sqft area.
        feeOfferGroups = areaGroups
          .map(g => {
            const rateSqft = parseFloat(offer.groupRates[g.id]) || 0;
            const rate = cvOfferRate(rateSqft);
            const area = Number(g.constructedArea) || 0;
            const fee = Math.round(rateSqft * (g.constructedAreaSqft || 0));
            return { label: g.label, constructedArea: area, rate, fee };
          })
          .filter(g => g.rate > 0 || g.constructedArea > 0);
      }
      totalDesignFee = feeOfferGroups.reduce((s, g) => s + g.fee, 0);
    } else if (offer?.groups || offer?.lines) {
      feeOfferGroups = (offer.groups || offer.lines || []).map(g => ({
        label: g.label || g.name || '',
        constructedArea: g.constructedArea ?? g.area ?? '',
        rate: g.rate ?? 0,
        fee: g.fee ?? g.amount ?? 0,
      }));
      totalDesignFee = offer?.totalFee ?? offer?.total ?? feeOfferGroups.reduce((s, g) => s + (Number(g.fee) || 0), 0);
    }

    return {
      docType: contract.docType,
      office: contract.office,
      date: formatDate(new Date()),
      projectName: inquiry?.projectName || '',
      clientName: inquiry?.invoicingName || inquiry?.clientNameOverride || inquiry?.clientName || '',
      clientAddress: inquiry?.invoicingAddress || inquiry?.clientAddressOverride || '',
      designBriefProjectType: contract.designBriefProjectType || inquiry?.projectType || '',
      designBriefLocation: contract.designBriefLocation || inquiry?.locationCity || '',
      designBriefCustomText: contract.designBriefCustomText || '',
      areaGroups,
      totalConstructedArea,
      feeOfferGroups,
      totalDesignFee,
      constructionCoordFee: contract.constructionCoordFee || 0,
      expensesText: contract.expensesText || '',
      paymentStages: (contract.feeStageOverrides || []).map(s => ({
        name: s.stageName || s.name || '',
        proportion: s.proportion || '',
        cumulative: s.cumulative || '',
      })),
      validityMonths: contract.validityMonths ?? 2,
      contractSettings: contract.contractSettings || {},
      // Sort excluded services into canonical STANDARD_SERVICES order so the PDF
      // always matches the defined list order, regardless of toggle history.
      excludedServices: [...STANDARD_SERVICES, ...PMC_SUBITEMS]
        .filter(s => (contract.excludedServices || []).includes(s)),
      isAppointmentLetter: contract.docType === 'appointment_letter',
      versionName: contract.versionName || '',
      signatoryName: contract.signatoryName || '',
      signatoryTitle: contract.signatoryTitle || '',
      signatoryDin: contract.signatoryDin || '',
      headerImageUrl: resolveBrandingImageSrc(settingsHook?.invoiceBranding?.offices?.[contract.office === 'rotterdam' ? 'rotterdam' : 'mumbai']?.headerImage),
      officeName: settingsHook?.invoiceBranding?.offices?.[contract.office === 'rotterdam' ? 'rotterdam' : 'mumbai']?.label || '',
      pdfStyles: settingsHook?.scopePdfStyles || { fontFamily: 'Raleway', primaryColor: '#9F902A' },
    };
  }, [settingsHook]);

  const renderHtml = useCallback((contract, inquiry) => {
    // Merge localContractSettings so PDF/preview always reflects current UI state.
    const merged = { ...contract, contractSettings: localContractSettings };
    const data = buildContractData(merged, inquiry);
    if (!data) return '';
    return contract.office === 'rotterdam' ? buildContractHtmlNL(data) : buildContractHtml(data);
  }, [buildContractData, localContractSettings]);

  const handlePreview = useCallback(() => {
    if (!selectedContract) return;
    setPreviewHtml(renderHtml(selectedContract, linkedInquiry));
  }, [selectedContract, linkedInquiry, renderHtml]);

  const handleDownload = useCallback(async () => {
    if (!selectedContract) return;
    setExporting(true);
    try {
      const html = renderHtml(selectedContract, linkedInquiry);
      const isLetter = selectedContract.docType === 'appointment_letter';
      const filename = makePdfFilename({
        projectName: linkedInquiry?.projectName,
        location: selectedContract.designBriefLocation || linkedInquiry?.locationCity,
        type: isLetter ? 'Appointment-Letter' : 'Consultancy-Offer',
        status: selectedContract.status ? selectedContract.status.charAt(0).toUpperCase() + selectedContract.status.slice(1) : 'Draft',
        version: selectedContract.versionName,
      });
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) { alert('Session expired. Please log in again.'); return; }
      const response = await fetch('/api/contract-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ html, filename }),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      alert('Failed to export PDF: ' + err.message);
    } finally {
      setExporting(false);
    }
  }, [selectedContract, linkedInquiry, renderHtml]);

  const buildTcPageHtml = useCallback((bodyHtml = null) => {
    const headerImageUrl = resolveBrandingImageSrc(settingsHook?.invoiceBranding?.offices?.[selectedContract?.office === 'rotterdam' ? 'rotterdam' : 'mumbai']?.headerImage);
    const pdfStyles = settingsHook?.scopePdfStyles || { fontFamily: 'Raleway', primaryColor: '#9F902A' };
    return buildStandardTcHtml({ headerImageUrl, pdfStyles, bodyHtml });
  }, [selectedContract, settingsHook]);

  const handleTcPreview = useCallback((bodyHtml = null) => {
    if (!selectedContract) return;
    setTcPreviewHtml(buildTcPageHtml(bodyHtml));
  }, [selectedContract, buildTcPageHtml]);

  const handleTcDownload = useCallback(async (bodyHtml = null, label = null) => {
    setTcExporting(true);
    try {
      const html = buildTcPageHtml(bodyHtml);
      const filename = makePdfFilename({
        projectName: linkedInquiry?.projectName || label,
        location: selectedContract?.designBriefLocation || linkedInquiry?.locationCity,
        type: 'Terms-and-Conditions',
        status: selectedContract?.status ? selectedContract.status.charAt(0).toUpperCase() + selectedContract.status.slice(1) : '',
        version: selectedContract?.versionName,
      });
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) { alert('Session expired. Please log in again.'); return; }
      const response = await fetch('/api/contract-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ html, filename }),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      alert('Failed to export PDF: ' + err.message);
    } finally {
      setTcExporting(false);
    }
  }, [buildTcPageHtml]);

  const handleField = useCallback(async (field, value) => {
    if (!selectedContract) return;
    if (field === 'contractSettings') {
      setLocalContractSettings(value);
    }
    const res = await updateContract(selectedContract.id, { [field]: value });
    if (res.success) {
      setSaveMsg('Saved');
      setTimeout(() => setSaveMsg(null), 1500);
    } else {
      setSaveMsg(res.error);
    }
  }, [selectedContract, updateContract]);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm('Delete this contract?')) return;
    const res = await deleteContract(id);
    if (res.success && selectedId === id) setSelectedId(null);
  }, [deleteContract, selectedId]);

  // ── List-view quick PDF helpers ──────────────────────────────────────────
  // Renders the contract HTML without merging localContractSettings (which
  // belongs to the detail view of the currently-selected contract only).
  const renderHtmlForContract = useCallback((contract, inquiry) => {
    const data = buildContractData(contract, inquiry);
    if (!data) return '';
    return contract.office === 'rotterdam' ? buildContractHtmlNL(data) : buildContractHtml(data);
  }, [buildContractData]);

  const buildListScopeHtml = useCallback((contract, inquiry) => {
    const officeKey = contract.office === 'rotterdam' ? 'rotterdam' : 'mumbai';
    const headerImageUrl = resolveBrandingImageSrc(settingsHook?.invoiceBranding?.offices?.[officeKey]?.headerImage);
    const pdfStyles = settingsHook?.scopePdfStyles || { fontFamily: 'Raleway', primaryColor: '#9F902A' };
    const paymentStages = (contract.feeStageOverrides || []).map(s => ({
      name: s.stageName || s.name || '',
      proportion: s.proportion || '',
      cumulative: s.cumulative || '',
    }));
    // If a SOW template was applied to this contract, render the full template
    // document (with stage descriptions and deliverables) plus the fee schedule.
    const tpl = contract.sowTemplateId
      ? (scopeTemplatesHook?.templates || []).find(t => String(t.id) === String(contract.sowTemplateId))
      : null;
    if (tpl) {
      // Map template stage shape → buildScopeHtml shape:
      // - template stores standardDeliverables/optionalDeliverables (no `included`, no `selected`)
      // - buildScopeHtml needs selected:true and deliverables.{standard,optional} with included:true
      const mappedStages = (tpl.stages || []).map(s => ({
        ...s,
        selected: true,
        deliverables: {
          standard: (s.standardDeliverables || s.deliverables?.standard || []).map(d => ({ ...d, included: true })),
          optional: (s.optionalDeliverables || s.deliverables?.optional || []).map(d => ({ ...d, included: true })),
        },
      }));
      return buildScopeHtml({
        title: tpl.name || 'Scope of Work',
        version: tpl.version,
        projectName: inquiry?.projectName || '',
        clientName: inquiry?.clientNameOverride || inquiry?.clientName || '',
        templateIntroduction: tpl.templateIntroduction,
        interiorWarmShellSelected: tpl.interiorWarmShellSelected,
        interiorFfeSelected: tpl.interiorFfeSelected,
        interiorIntroduction: tpl.interiorIntroduction,
        interiorDeliverables: tpl.interiorDeliverables,
        stages: mappedStages,
        paymentStages,
        pdfStyles,
        headerImageUrl,
        officeName: settingsHook?.invoiceBranding?.offices?.[officeKey]?.label || '',
      });
    }
    // Fallback: fee schedule only (no template linked yet)
    return buildScopeHtml({
      title: 'Scope of Work',
      projectName: inquiry?.projectName || '',
      clientName: inquiry?.clientNameOverride || inquiry?.clientName || '',
      stages: [],
      paymentStages,
      pdfStyles,
      headerImageUrl,
      officeName: settingsHook?.invoiceBranding?.offices?.[officeKey]?.label || '',
    });
  }, [settingsHook, scopeTemplatesHook]);

  const buildListTcHtml = useCallback((contract) => {
    const officeKey = contract.office === 'rotterdam' ? 'rotterdam' : 'mumbai';
    const headerImageUrl = resolveBrandingImageSrc(settingsHook?.invoiceBranding?.offices?.[officeKey]?.headerImage);
    const pdfStyles = settingsHook?.scopePdfStyles || { fontFamily: 'Raleway', primaryColor: '#9F902A' };
    let bodyHtml = null;
    if (contract.tcTemplateId != null) {
      // A specific saved version is applied — use its content
      const tpl = tcTemplates.find(t => t.id === contract.tcTemplateId);
      bodyHtml = tpl?.content || null;
    } else if (contract.tcType === 'client_own' || (contract.tcType === 'modified' && contract.tcCustomContent)) {
      bodyHtml = contract.tcCustomContent;
    }
    return buildStandardTcHtml({ headerImageUrl, pdfStyles, bodyHtml });
  }, [settingsHook, tcTemplates]);

  const handleListQuickPreview = useCallback((contract, type) => {
    const inq = inquiryById(contract.inquiryId);
    const loc = contract.designBriefLocation || inq?.locationCity;
    const statusLabel = contract.status ? contract.status.charAt(0).toUpperCase() + contract.status.slice(1) : '';
    let html, title, filename;
    if (type === 'offer') {
      html = renderHtmlForContract(contract, inq);
      const isLetter = contract.docType === 'appointment_letter';
      title = isLetter ? 'Appointment Letter' : 'Consultancy Offer';
      filename = makePdfFilename({ projectName: inq?.projectName, location: loc, type: isLetter ? 'Appointment-Letter' : 'Consultancy-Offer', status: statusLabel, version: contract.versionName });
    } else if (type === 'scope') {
      html = buildListScopeHtml(contract, inq);
      title = 'Scope of Work';
      filename = makePdfFilename({ projectName: inq?.projectName, location: loc, type: 'Scope-of-Work', status: statusLabel, version: contract.versionName });
    } else {
      html = buildListTcHtml(contract);
      title = 'Terms & Conditions';
      filename = makePdfFilename({ projectName: inq?.projectName, location: loc, type: 'Terms-and-Conditions', status: statusLabel, version: contract.versionName });
    }
    setListQuickPdf({ html, title, filename });
  }, [inquiryById, renderHtmlForContract, buildListScopeHtml, buildListTcHtml]);

  const handleListQuickDownload = useCallback(async () => {
    if (!listQuickPdf) return;
    setListQuickExporting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) { alert('Session expired. Please log in again.'); return; }
      const response = await fetch('/api/contract-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ html: listQuickPdf.html, filename: listQuickPdf.filename }),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = listQuickPdf.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      alert('Failed to export PDF: ' + err.message);
    } finally {
      setListQuickExporting(false);
    }
  }, [listQuickPdf]);

  // ========================================================================
  // DETAIL VIEW
  // ========================================================================
  if (selectedContract) {
    return (
      <div>
        <BrioPageHeader
          title="Contracts"
          subtitle={[linkedInquiry?.projectName, selectedContract?.contractName, selectedContract?.versionName].filter(Boolean).join(' · ') || 'Contract'}
        />
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <BrioButton variant="secondary" onClick={() => setSelectedId(null)}>
              <ArrowLeft size={14} /> Back to list
            </BrioButton>
            {saveMsg && <BrioBadge variant="success">{saveMsg}</BrioBadge>}
          </div>
          <div className="flex gap-2">
            {['offer', 'scope', 'tc'].map(tab => (
              <BrioButton
                key={tab}
                variant={activeTab === tab ? 'primary' : 'secondary'}
                onClick={() => setActiveTab(tab)}
              >
                {tab === 'offer' ? 'Offer' : tab === 'scope' ? 'Scope of Work' : 'Terms & Conditions'}
              </BrioButton>
            ))}
          </div>
        </div>
        {activeTab === 'offer' && (
          <OfferTabLayoutC
            contract={selectedContract}
            inquiry={linkedInquiry}
            onField={handleField}
            onInquiryField={handleInquiryField}
            onPreview={handlePreview}
            onDownload={handleDownload}
            exporting={exporting}
            onNavigateScope={() => setActiveTab('scope')}
          />
        )}

        {activeTab === 'scope' && (
          <ScopeTab
            contract={selectedContract}
            onField={handleField}
            scopeTemplatesHook={scopeTemplatesHook}
            projectScopes={projectScopes}
            onNavigateToTemplates={() => onNavigate('contracts-scope')}
            settingsHook={settingsHook}
            allContracts={contracts}
            allInquiries={allInquiries}
            activeTasks={activeTasks}
          />
        )}

        {activeTab === 'tc' && (
          <TcTab
            contract={selectedContract}
            templates={tcTemplates}
            onField={handleField}
            onPreview={handleTcPreview}
            onDownload={handleTcDownload}
            exporting={tcExporting}
            onCreateTemplate={createTcTemplate}
            onUpdateTemplate={updateTcTemplate}
            onDeleteTemplate={deleteTcTemplate}
          />
        )}

        {previewHtml && (
          <BrioModal onClose={() => setPreviewHtml(null)} title="Contract Preview" width="xl">
            <iframe
              title="contract-preview"
              srcDoc={previewHtml}
              style={{ width: '100%', height: '70vh', border: '1px solid var(--brio-border)' }}
            />
            <div className="flex justify-end mt-3">
              <BrioButton variant="primary" onClick={handleDownload} disabled={exporting}>
                <Download size={14} /> {exporting ? 'Generating...' : 'Download PDF'}
              </BrioButton>
            </div>
          </BrioModal>
        )}

        {tcPreviewHtml && (
          <BrioModal onClose={() => setTcPreviewHtml(null)} title="Terms & Conditions Preview" width="xl">
            <iframe
              title="tc-preview"
              srcDoc={tcPreviewHtml}
              style={{ width: '100%', height: '70vh', border: '1px solid var(--brio-border)' }}
            />
            <div className="flex justify-end mt-3">
              <BrioButton variant="primary" onClick={handleTcDownload} disabled={tcExporting}>
                <Download size={14} /> {tcExporting ? 'Generating...' : 'Download PDF'}
              </BrioButton>
            </div>
          </BrioModal>
        )}
      </div>
    );
  }

  // ========================================================================
  // LIST VIEW
  // ========================================================================
  return (
    <div>
      <BrioPageHeader
        title="Contracts"
        subtitle="Consultancy offers & appointment letters"
        right={
          <BrioButton variant="primary" onClick={() => setShowCreate(true)}>
            <Plus size={14} /> New Contract
          </BrioButton>
        }
      />

      {error && <BrioAlert label="Error">{error}</BrioAlert>}

      <BrioFrame>
        {loading ? (
          <div className="p-6 text-center" style={{ color: 'var(--brio-text-muted)' }}>Loading...</div>
        ) : contracts.length === 0 ? (
          <div className="p-6 text-center" style={{ color: 'var(--brio-text-muted)' }}>
            No contracts yet. Click "New Contract" to begin.
          </div>
        ) : (
          <BrioAnalyticsSurface padding="m">
            <BrioTable style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSortClick('project')}>project{getSortIndicator('project')}</th>
                  <th style={{ cursor: 'pointer', maxWidth: 140 }} onClick={() => handleSortClick('contractName')}>name{getSortIndicator('contractName')}</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSortClick('versionName')}>version{getSortIndicator('versionName')}</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSortClick('client')}>client{getSortIndicator('client')}</th>
                  <th style={{ cursor: 'pointer', width: 1, whiteSpace: 'nowrap' }} onClick={() => handleSortClick('office')}>office{getSortIndicator('office')}</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSortClick('status')}>status{getSortIndicator('status')}</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSortClick('docType')}>type{getSortIndicator('docType')}</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSortClick('createdAt')}>created{getSortIndicator('createdAt')}</th>
                  <th style={{ width: 1, whiteSpace: 'nowrap', textAlign: 'center', padding: '0 3px' }} title="Appointment letter / Consultancy offer">ltr</th>
                  <th style={{ width: 1, whiteSpace: 'nowrap', textAlign: 'center', padding: '0 3px' }} title="Scope of work">sow</th>
                  <th style={{ width: 1, whiteSpace: 'nowrap', textAlign: 'center', padding: '0 3px' }} title="Terms & conditions">t&amp;c</th>
                  <th className="text-center col-actions">actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedContracts.map(c => {
                  const inq = inquiryById(c.inquiryId);
                  return (
                    <tr key={c.id} className="brio-row--hoverable">
                      <td>{inq?.projectName || `Inquiry #${c.inquiryId}`}</td>
                      <td style={{ maxWidth: 140, overflow: 'hidden' }}>
                        <input
                          style={{ background: 'transparent', border: 'none', outline: 'none', fontSize: 'inherit', fontFamily: 'inherit', color: 'inherit', padding: 0, width: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          placeholder="Contract name"
                          defaultValue={c.contractName || ''}
                          onBlur={e => updateContract(c.id, { contractName: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          style={{ background: 'transparent', border: 'none', outline: 'none', fontSize: 'inherit', fontFamily: 'inherit', color: 'inherit', padding: 0, width: '100%' }}
                          placeholder="Version"
                          defaultValue={c.versionName || ''}
                          onBlur={e => updateContract(c.id, { versionName: e.target.value })}
                        />
                      </td>
                      <td>{inq?.clientNameOverride || inq?.clientName || '-'}</td>
                      <td style={{ width: 1, whiteSpace: 'nowrap' }}>{OFFICE_LABEL[c.office] || c.office}</td>
                      <td><BrioBadge variant={STATUS_VARIANT[c.status] || 'neutral'}>{c.status}</BrioBadge></td>
                      <td>{c.docType === 'appointment_letter' ? 'App. Letter' : 'Offer'}</td>
                      <td>{formatDate(c.createdAt)}</td>
                      <td style={{ textAlign: 'center', width: 1, whiteSpace: 'nowrap', padding: '0 3px' }}>
                        {(() => {
                          const ready = isContractFullyReviewed(c);
                          return (
                            <button
                              className="brio-icon-btn brio-icon-btn--small"
                              title={ready ? (c.docType === 'appointment_letter' ? 'Preview appointment letter' : 'Preview consultancy offer') : 'Complete all offer tab review checks first'}
                              disabled={!ready}
                              onClick={() => handleListQuickPreview(c, 'offer')}
                              type="button"
                            >
                              <FileText size={12} />
                            </button>
                          );
                        })()}
                      </td>
                      <td style={{ textAlign: 'center', width: 1, whiteSpace: 'nowrap', padding: '0 3px' }}>
                        <button
                          className="brio-icon-btn brio-icon-btn--small"
                          title={c.sowTemplateId ? 'Preview scope of work' : 'No SOW template linked — open contract → Scope tab'}
                          disabled={!c.sowTemplateId}
                          onClick={() => handleListQuickPreview(c, 'scope')}
                          type="button"
                        >
                          <FileText size={12} />
                        </button>
                      </td>
                      <td style={{ textAlign: 'center', width: 1, whiteSpace: 'nowrap', padding: '0 3px' }}>
                        <button
                          className="brio-icon-btn brio-icon-btn--small"
                          title="Preview terms & conditions"
                          onClick={() => handleListQuickPreview(c, 'tc')}
                          type="button"
                        >
                          <FileText size={12} />
                        </button>
                      </td>
                      <td className="text-center">
                        <BrioActionGroup center>
                          <button className="brio-icon-btn" title="Open contract" onClick={() => { setSelectedId(c.id); setActiveTab('offer'); }} type="button">
                            <Pencil size={14} />
                          </button>
                          <button className="brio-icon-btn" title="Duplicate" onClick={() => handleDuplicate(c.id)} type="button">
                            <Copy size={14} />
                          </button>
                          <button className="brio-icon-btn brio-icon-btn--delete" title="Delete" onClick={() => handleDelete(c.id)} type="button">
                            <Trash2 size={14} />
                          </button>
                        </BrioActionGroup>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </BrioTable>
          </BrioAnalyticsSurface>
        )}
      </BrioFrame>

      {listQuickPdf && (
        <BrioModal onClose={() => { setListQuickPdf(null); setListQuickExporting(false); }} title={`${listQuickPdf.title} — Preview`} width="xl">
          <iframe
            title="list-pdf-preview"
            srcDoc={listQuickPdf.html}
            style={{ width: '100%', height: '70vh', border: '1px solid var(--brio-border)' }}
          />
          <div className="flex justify-end mt-3">
            <BrioButton variant="primary" onClick={handleListQuickDownload} disabled={listQuickExporting}>
              <Download size={14} /> {listQuickExporting ? 'Generating...' : 'Download PDF'}
            </BrioButton>
          </div>
        </BrioModal>
      )}

      {showCreate && (
        <BrioModal onClose={() => setShowCreate(false)} title="New Contract">
          <div className="space-y-3">
            <label className="block">
              <span className="block mb-1" style={{ fontSize: 'var(--brio-font-size-s)' }}>Inquiry / Project</span>
              <BrioSelect value={newInquiryId} onChange={e => setNewInquiryId(e.target.value)}>
                <option value="">Select inquiry...</option>
                {inquiries.map(i => (
                  <option key={i.id} value={i.id}>{i.projectName || `Inquiry #${i.id}`}</option>
                ))}
              </BrioSelect>
            </label>
            <label className="block">
              <span className="block mb-1" style={{ fontSize: 'var(--brio-font-size-s)' }}>Office</span>
              <BrioSelect value={newOffice} onChange={e => setNewOffice(e.target.value)}>
                <option value="mumbai">Mumbai</option>
                <option value="rotterdam">Rotterdam</option>
              </BrioSelect>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="block mb-1" style={{ fontSize: 'var(--brio-font-size-s)' }}>Contract name (optional)</span>
                <BrioInput value={newContractName} onChange={e => setNewContractName(e.target.value)} placeholder="e.g. Design Offer" />
              </label>
              <label className="block">
                <span className="block mb-1" style={{ fontSize: 'var(--brio-font-size-s)' }}>Version</span>
                <BrioInput value={newVersionName} onChange={e => setNewVersionName(e.target.value)} placeholder="e.g. V1.0" />
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <BrioButton variant="secondary" onClick={() => setShowCreate(false)}>Cancel</BrioButton>
              <BrioButton variant="primary" onClick={handleCreate} disabled={!newInquiryId}>Create</BrioButton>
            </div>
          </div>
        </BrioModal>
      )}
    </div>
  );
}

// ── Arrow-key navigation for editable table grids ──────────────────────────
// Attach to any container div wrapping a set of grid rows. Arrow Up/Down move
// to the same column in the adjacent row. Arrow Left/Right move to the adjacent
// cell on the same row (only when the text cursor is already at the edge of the
// input value, so normal cursor movement inside a field is not affected).
function useArrowTableNav(containerRef) {
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const SELECTOR = [
      'input:not([disabled]):not([type="checkbox"]):not([type="radio"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
    ].join(', ');
    const handler = (e) => {
      const { key } = e;
      if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) return;
      const active = document.activeElement;
      if (!container.contains(active) || !active.matches(SELECTOR)) return;
      // For left/right, only navigate when the cursor is already at the edge
      if (key === 'ArrowLeft' && (active.selectionStart ?? 0) !== 0) return;
      if (key === 'ArrowRight' && (active.selectionStart ?? 0) !== (active.value?.length ?? 0)) return;
      const ar = active.getBoundingClientRect();
      const inputs = Array.from(container.querySelectorAll(SELECTOR)).filter(i => i !== active);
      let target = null;
      let best = Infinity;
      for (const inp of inputs) {
        const r = inp.getBoundingClientRect();
        let score = Infinity;
        if (key === 'ArrowDown') {
          if (r.top < ar.bottom - 4) continue;
          score = (r.top - ar.bottom) * 10 + Math.abs(r.left - ar.left);
        } else if (key === 'ArrowUp') {
          if (r.bottom > ar.top + 4) continue;
          score = (ar.top - r.bottom) * 10 + Math.abs(r.left - ar.left);
        } else if (key === 'ArrowRight') {
          if (r.left < ar.right - 4) continue;
          score = Math.abs(r.top - ar.top) < ar.height * 0.6 ? r.left - ar.right : Infinity;
        } else if (key === 'ArrowLeft') {
          if (r.right > ar.left + 4) continue;
          score = Math.abs(r.top - ar.top) < ar.height * 0.6 ? ar.left - r.right : Infinity;
        }
        if (score < best) { best = score; target = inp; }
      }
      if (target) {
        e.preventDefault();
        target.focus();
        if (typeof target.select === 'function') target.select();
      }
    };
    container.addEventListener('keydown', handler);
    return () => container.removeEventListener('keydown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

// ============================================================================
// SCOPE TAB
// ============================================================================
// Helpers for stage name detection
const isCCStage = (name) => {
  const n = (name || '').toLowerCase().trim();
  return n === 'cc' || n.startsWith('cc ') || n.includes('construction co');
};
const isAdvanceStage = (name) => (name || '').toLowerCase().trim() === 'advance';
const isGFCStage = (name) => (name || '').toLowerCase().includes('gfc');
const isCTStage = (name) => /^ct[\s\d]/i.test((name || '').trim());
const extractCTNum = (name) => { const m = (name || '').match(/^ct[\s]*(\d+)/i); return m ? parseInt(m[1], 10) : null; };

// Deduplicate CT stages from a list by CT number, keeping first occurrence of each number.
const deduplicateCTStages = (stages, getName) => {
  const seen = new Set();
  return stages.filter(s => {
    const n = extractCTNum(getName(s));
    if (n === null) return true;
    if (seen.has(n)) return false;
    seen.add(n);
    return true;
  });
};

// Sortable row for payment stages DnD
function SortableStageRow({ id, stage, idx, updateStage, removeStage }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className="grid grid-cols-12 gap-2 mb-2 items-center">
      <div
        className="col-span-1 flex items-center justify-center"
        style={{ cursor: 'grab', color: 'var(--brio-text-muted)', touchAction: 'none' }}
        {...attributes}
        {...listeners}
      >
        <GripVertical size={15} />
      </div>
      <BrioInput className="col-span-6" placeholder="Stage name" value={stage.stageName || ''} onChange={e => updateStage(idx, 'stageName', e.target.value)} />
      <BrioInput className="col-span-2" placeholder="e.g. 10%" value={stage.proportion || ''} onChange={e => updateStage(idx, 'proportion', e.target.value)} />
      <BrioInput className="col-span-2" placeholder="e.g. 25%" value={stage.cumulative || ''} onChange={e => updateStage(idx, 'cumulative', e.target.value)} />
      <div className="col-span-1 flex justify-end">
        <BrioButton variant="secondary" onClick={() => removeStage(idx)}><Trash2 size={14} /></BrioButton>
      </div>
    </div>
  );
}

function ScopeTab({ contract, onField, scopeTemplatesHook, projectScopes, onNavigateToTemplates, settingsHook, activeTasks }) {
  const office = contract?.office || 'mumbai';
  const stages = contract?.feeStageOverrides || [];
  const [selectedTplId, setSelectedTplId] = useState(contract?.sowTemplateId || '');
  // Keep selector in sync if contract.sowTemplateId changes (e.g. switching contracts)
  React.useEffect(() => {
    setSelectedTplId(contract?.sowTemplateId || '');
  }, [contract?.sowTemplateId]);
  const [selectedPropTplId, setSelectedPropTplId] = useState('');
  const [showSavePropTpl, setShowSavePropTpl] = useState(false);
  const [newPropTplName, setNewPropTplName] = useState('');
  const [renamingPropTplId, setRenamingPropTplId] = useState(null);
  const [renamePropTplValue, setRenamePropTplValue] = useState('');
  const [propTplMsg, setPropTplMsg] = useState(null);
  const stagesTableRef = React.useRef(null);
  useArrowTableNav(stagesTableRef);

  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const confirmedTemplates = (scopeTemplatesHook?.templates || []).filter(t => t.status === 'confirmed');
  const proportionTemplates = settingsHook?.appSettings?.proportion_templates || [];

  const recalcCumulative = (stageList) => {
    let running = 0;
    return stageList.map(s => {
      const pct = parseFloat(String(s.proportion).replace('%', '')) || 0;
      running += pct;
      const cum = running % 1 === 0 ? `${Math.round(running)}%` : `${running.toFixed(1)}%`;
      return { ...s, cumulative: cum };
    });
  };


  const updateStage = (idx, key, value) => {
    const next = stages.map((s, i) => (i === idx ? { ...s, [key]: value } : s));
    if (key === 'proportion') {
      onField('feeStageOverrides', recalcCumulative(next));
    } else {
      onField('feeStageOverrides', next);
    }
  };
  const addStage = () => onField('feeStageOverrides', [...stages, { stageName: '', proportion: '', cumulative: '' }]);
  const removeStage = (idx) => onField('feeStageOverrides', stages.filter((_, i) => i !== idx));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = stages.findIndex((_, i) => `stage-${i}` === active.id);
    const newIdx = stages.findIndex((_, i) => `stage-${i}` === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    onField('feeStageOverrides', recalcCumulative(arrayMove(stages, oldIdx, newIdx)));
  };

  const applyEqualProportions = () => {
    if (stages.length === 0) return;
    const each = Math.floor(100 / stages.length);
    const remainder = 100 - each * stages.length;
    let running = 0;
    const next = stages.map((s, i) => {
      const pct = each + (i < remainder ? 1 : 0);
      running += pct;
      return { ...s, proportion: `${pct}%`, cumulative: `${running}%` };
    });
    onField('feeStageOverrides', next);
  };

  const loadSowTemplate = (tplId) => {
    setSelectedTplId(tplId);
    onField('sowTemplateId', tplId || null);
    if (!tplId) return;

    if (tplId.startsWith('scope:')) {
      // Load stage names from a project scope version
      const scopeId = tplId.slice(6);
      const scope = (projectScopes || []).find(s => s.id === scopeId);
      if (!scope) return;
      const allScopeStages = scope.stages.filter(s => s.selected);
      const source = allScopeStages.length > 0 ? allScopeStages : scope.stages;
      // Filter out CC and CT stages from the scope (same as regular template path)
      let newStages = source
        .filter(s => !isCCStage(s.fullName || s.name || '') && !isCTStage(s.fullName || s.name || ''))
        .map(s => ({ stageName: s.fullName || s.name || '', proportion: '', cumulative: '' }));
      // Ensure Advance is first
      if (!newStages.some(s => isAdvanceStage(s.stageName))) {
        newStages = [{ stageName: 'Advance', proportion: '', cumulative: '' }, ...newStages];
      }
      // Re-add CT stages from activeTasks — deduplicated by CT number to avoid duplicates
      // when the task DB has both short ("CT 1") and long ("CT1 (Construction Time 1)") variants.
      const rawCT = (activeTasks || []).filter(s => isCTStage(s.name)).sort((a, b) => (a.order ?? a.id) - (b.order ?? b.id));
      const ctTaskStages = deduplicateCTStages(rawCT, s => s.name).map(s => ({ stageName: `Construction Time ${extractCTNum(s.name)}`, proportion: '', cumulative: '' }));
      if (ctTaskStages.length > 0) {
        let lastGfcIdx = -1;
        newStages.forEach((s, i) => { if (isGFCStage(s.stageName)) lastGfcIdx = i; });
        const insertAt = lastGfcIdx >= 0 ? lastGfcIdx + 1 : newStages.length;
        newStages = [...newStages.slice(0, insertAt), ...ctTaskStages, ...newStages.slice(insertAt)];
      }
      onField('feeStageOverrides', newStages);
      return;
    }

    const tpl = confirmedTemplates.find(t => String(t.id) === String(tplId));
    if (!tpl || !Array.isArray(tpl.stages)) return;

    // Filter out CC stages (retainer) and CT stages (re-added fresh from activeTasks)
    let newStages = tpl.stages
      .filter(s => !isCCStage(s.fullName || s.name || '') && !isCTStage(s.fullName || s.name || ''))
      .map(s => ({ stageName: s.fullName || s.name || '', proportion: '', cumulative: '' }));

    // Ensure Advance is first
    if (!newStages.some(s => isAdvanceStage(s.stageName))) {
      newStages = [{ stageName: 'Advance', proportion: '', cumulative: '' }, ...newStages];
    }

    // Insert CT stages from activeTasks after the last GFC stage (or at end)
    // Deduplicate by CT number so "CT 1" and "CT1 (Construction Time 1)" don't both appear.
    const rawCT2 = (activeTasks || []).filter(s => isCTStage(s.name)).sort((a, b) => (a.order ?? a.id) - (b.order ?? b.id));
    const ctTaskStages = deduplicateCTStages(rawCT2, s => s.name).map(s => ({ stageName: `Construction Time ${extractCTNum(s.name)}`, proportion: '', cumulative: '' }));

    if (ctTaskStages.length > 0) {
      let lastGfcIdx = -1;
      newStages.forEach((s, i) => { if (isGFCStage(s.stageName)) lastGfcIdx = i; });
      const insertAt = lastGfcIdx >= 0 ? lastGfcIdx + 1 : newStages.length;
      newStages = [...newStages.slice(0, insertAt), ...ctTaskStages, ...newStages.slice(insertAt)];
    }

    onField('feeStageOverrides', newStages);
  };

  const applyProportionTemplate = (tplId) => {
    setSelectedPropTplId(tplId);
    const tpl = proportionTemplates.find(t => t.id === tplId);
    if (!tpl || stages.length === 0) return;
    const entryMap = (tpl.entries || []).reduce((m, e) => { m[e.stageName] = e.proportion; return m; }, {});
    const next = stages.map(s => ({ ...s, proportion: entryMap[s.stageName] ?? s.proportion }));
    onField('feeStageOverrides', recalcCumulative(next));
  };

  const saveProportionTemplate = async (name) => {
    if (!name.trim() || !settingsHook?.updateSetting || stages.length === 0) return;
    const entries = stages.map(s => ({ stageName: s.stageName, proportion: s.proportion }));
    const id = 'pt-' + Date.now();
    const updated = [...proportionTemplates, { id, name: name.trim(), entries }];
    await settingsHook.updateSetting('proportion_templates', updated);
    setNewPropTplName('');
    setShowSavePropTpl(false);
    setSelectedPropTplId(id);
    setPropTplMsg('Saved!');
    setTimeout(() => setPropTplMsg(null), 2000);
  };

  const renamePropTemplate = async (id, name) => {
    if (!name.trim() || !settingsHook?.updateSetting) return;
    const updated = proportionTemplates.map(t => t.id === id ? { ...t, name: name.trim() } : t);
    await settingsHook.updateSetting('proportion_templates', updated);
    setRenamingPropTplId(null);
  };

  const deletePropTemplate = async (id) => {
    if (!settingsHook?.updateSetting) return;
    const updated = proportionTemplates.filter(t => t.id !== id);
    await settingsHook.updateSetting('proportion_templates', updated);
    if (selectedPropTplId === id) setSelectedPropTplId('');
  };

  const updatePropTemplate = async (id) => {
    if (!settingsHook?.updateSetting || stages.length === 0) return;
    const entries = stages.map(s => ({ stageName: s.stageName, proportion: s.proportion }));
    const updated = proportionTemplates.map(t => t.id === id ? { ...t, entries } : t);
    await settingsHook.updateSetting('proportion_templates', updated);
    setPropTplMsg('Saved!');
    setTimeout(() => setPropTplMsg(null), 2000);
  };

  const duplicatePropTemplate = async (id) => {
    if (!settingsHook?.updateSetting) return;
    const tpl = proportionTemplates.find(t => t.id === id);
    if (!tpl) return;
    const newId = 'pt-' + Date.now();
    const newTpl = { ...tpl, id: newId, name: tpl.name + ' (copy)' };
    const updated = [...proportionTemplates, newTpl];
    await settingsHook.updateSetting('proportion_templates', updated);
    setSelectedPropTplId(newId);
    setPropTplMsg('Duplicated!');
    setTimeout(() => setPropTplMsg(null), 2000);
  };

  return (
    <div className="space-y-4">
      {/* Proportions controls */}
      <BrioCard>
        <h3 style={{ fontWeight: 600, marginBottom: 'var(--brio-space-s)' }}>Scope of Work Template</h3>
        <p style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)', marginBottom: 12 }}>
          Select a confirmed SOW template to populate stage names. Fill in proportions and cumulative percentages manually.
        </p>
        <div className="flex gap-2 items-end flex-wrap">
          <label className="block" style={{ flex: 1, minWidth: 200 }}>
            <span className="block mb-1" style={{ fontSize: 'var(--brio-font-size-s)' }}>Select SOW template</span>
            <BrioSelect value={selectedTplId} onChange={e => loadSowTemplate(e.target.value)}>
              <option value="">{confirmedTemplates.length === 0 && (!projectScopes || projectScopes.length === 0) ? '— no templates found —' : '— choose template —'}</option>
              {projectScopes && projectScopes.length > 0 && (
                <optgroup label="This project's scope documents">
                  {projectScopes.map(s => (
                    <option key={s.id} value={`scope:${s.id}`}>
                      {s.title || 'Scope of Work'} — v{s.version} ({s.status})
                    </option>
                  ))}
                </optgroup>
              )}
              {confirmedTemplates.length > 0 && (
                <optgroup label="Base templates">
                  {confirmedTemplates.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </optgroup>
              )}
            </BrioSelect>
          </label>
        </div>
        {confirmedTemplates.length === 0 && (!projectScopes || projectScopes.length === 0) && (
          <p style={{ fontSize: 'var(--brio-font-size-xs)', color: 'var(--brio-text-muted)', marginTop: 6 }}>
            Go to <strong>Contracts &amp; Scope</strong> to create a scope of work document or confirm a base template.
          </p>
        )}
      </BrioCard>

      {/* Payment stages editor */}
      <BrioCard>
        <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
          <div>
            <h3 style={{ fontWeight: 600 }}>Payment Stages</h3>
            <p style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)', marginTop: 2 }}>
              Stage names come from the SOW template above. Fill in proportions and cumulative percentages.
            </p>
          </div>
          <div className="flex gap-2">
            <BrioButton variant="secondary" onClick={addStage}><Plus size={14} /> Add Custom Stage</BrioButton>
          </div>
        </div>

        {/* Proportion templates strip */}
        {settingsHook?.updateSetting && (
          <div style={{
            background: 'var(--brio-gold-light)',
            border: '1px solid rgba(159,144,42,0.2)',
            borderRadius: 6,
            padding: '10px 14px',
            marginBottom: 16,
          }}>
            <div className="flex gap-2 items-center flex-wrap">
              <span style={{ fontSize: 'var(--brio-font-size-s)', fontWeight: 600, color: 'var(--brio-gold)', whiteSpace: 'nowrap' }}>
                Proportion templates
              </span>
              {renamingPropTplId ? (
                <>
                  <BrioInput
                    value={renamePropTplValue}
                    onChange={e => setRenamePropTplValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') renamePropTemplate(renamingPropTplId, renamePropTplValue); if (e.key === 'Escape') setRenamingPropTplId(null); }}
                    style={{ flex: 1, minWidth: 160, fontSize: 'var(--brio-font-size-s)' }}
                    autoFocus
                  />
                  <button className="brio-icon-btn brio-icon-btn--small brio-icon-btn--approve" onClick={() => renamePropTemplate(renamingPropTplId, renamePropTplValue)} title="Save name">
                    <CheckCircle2 size={14} />
                  </button>
                  <button className="brio-icon-btn brio-icon-btn--small" onClick={() => setRenamingPropTplId(null)} title="Cancel">
                    <Circle size={14} />
                  </button>
                </>
              ) : showSavePropTpl ? (
                <>
                  <BrioInput
                    placeholder="Template name"
                    value={newPropTplName}
                    onChange={e => setNewPropTplName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveProportionTemplate(newPropTplName); if (e.key === 'Escape') { setShowSavePropTpl(false); setNewPropTplName(''); } }}
                    style={{ flex: 1, minWidth: 160, fontSize: 'var(--brio-font-size-s)' }}
                    autoFocus
                  />
                  <button className="brio-icon-btn brio-icon-btn--small brio-icon-btn--approve" onClick={() => saveProportionTemplate(newPropTplName)} disabled={!newPropTplName.trim()} title="Save">
                    <CheckCircle2 size={14} />
                  </button>
                  <button className="brio-icon-btn brio-icon-btn--small" onClick={() => { setShowSavePropTpl(false); setNewPropTplName(''); }} title="Cancel">
                    <Circle size={14} />
                  </button>
                </>
              ) : (
                <>
                  <BrioSelect
                    value={selectedPropTplId}
                    onChange={e => { const id = e.target.value; if (id) applyProportionTemplate(id); else setSelectedPropTplId(''); }}
                    style={{ flex: 1, minWidth: 160, fontSize: 'var(--brio-font-size-s)' }}
                  >
                    <option value="">{proportionTemplates.length === 0 ? '— no templates saved yet —' : '— choose template —'}</option>
                    {proportionTemplates.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </BrioSelect>
                  {selectedPropTplId && (
                    <div className="brio-action-group">
                      <button className="brio-icon-btn brio-icon-btn--small brio-icon-btn--edit" onClick={() => { const tpl = proportionTemplates.find(t => t.id === selectedPropTplId); if (tpl) { setRenamingPropTplId(tpl.id); setRenamePropTplValue(tpl.name); } }} title="Rename">
                        <Pencil size={13} />
                      </button>
                      <button className="brio-icon-btn brio-icon-btn--small" onClick={() => updatePropTemplate(selectedPropTplId)} title="Save current proportions to template">
                        <Save size={13} />
                      </button>
                      <button className="brio-icon-btn brio-icon-btn--small" onClick={() => duplicatePropTemplate(selectedPropTplId)} title="Duplicate">
                        <Copy size={13} />
                      </button>
                      <button className="brio-icon-btn brio-icon-btn--small brio-icon-btn--delete" onClick={() => deletePropTemplate(selectedPropTplId)} title="Delete">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  )}
                  {stages.length > 0 && (
                    <button className="brio-icon-btn brio-icon-btn--small" onClick={() => setShowSavePropTpl(true)} title="Save current proportions as new template">
                      <Plus size={13} />
                    </button>
                  )}
                </>
              )}
              {propTplMsg && (
                <span style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-gold)', fontWeight: 600 }}>{propTplMsg}</span>
              )}
            </div>
          </div>
        )}

        {stages.length === 0 ? (
          <div style={{ color: 'var(--brio-text-muted)', fontSize: 'var(--brio-font-size-s)', padding: '12px 0' }}>
            No stages yet. Select a SOW template above or click "Add Stage" to add manually.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-12 gap-2 mb-1" style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)', fontWeight: 600 }}>
              <div className="col-span-1"></div>
              <div className="col-span-6">Stage Name</div>
              <div className="col-span-2">Proportion</div>
              <div className="col-span-2">Cumulative</div>
              <div className="col-span-1"></div>
            </div>
            <div ref={stagesTableRef}>
              <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={stages.map((_, i) => `stage-${i}`)} strategy={verticalListSortingStrategy}>
                  {stages.map((s, idx) => (
                    <SortableStageRow
                      key={`stage-${idx}`}
                      id={`stage-${idx}`}
                      stage={s}
                      idx={idx}
                      updateStage={updateStage}
                      removeStage={removeStage}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </div>
            {(() => {
              const total = stages.reduce((sum, s) => sum + (parseFloat(String(s.proportion || '').replace('%', '')) || 0), 0);
              const isValid = Math.abs(total - 100) < 0.1;
              return (
                <div style={{ marginTop: 8, padding: '6px 10px', background: isValid ? '#f0fdf4' : '#fef9ee', borderRadius: 4, fontSize: 'var(--brio-font-size-s)', fontWeight: 600, color: isValid ? '#16a34a' : '#d97706' }}>
                  Total: {total % 1 === 0 ? Math.round(total) : total.toFixed(1)}%{isValid ? ' ✓ equals 100%' : ' — must equal 100%'}
                </div>
              );
            })()}
          </>
        )}
      </BrioCard>
    </div>
  );
}

const OPTIONAL_BRIEF_POINTS = [
  'Use the natural potential of the site as far as possible by tying together functionality and creativity.',
  'Giving the house a relaxed feel by integrating the landscape with building and interior design. The house could merge with the landscape both physically and visually.',
  'The home must have a laid back and relaxed feel.',
  'Materials used in the home must to be locally sourced wherever possible, practical and easy to maintain/replace.',
];
const NL_OPTIONAL_BRIEF_POINTS = [
  'Use the natural potential of the site as far as possible by tying together functionality and creativity.',
  'Giving the house a relaxed feel by integrating the landscape with building and interior design. The house could merge with the landscape both physically and visually.',
  'The home must have a laid back and relaxed feel.',
  'Materials used in the home must to be locally sourced wherever possible, practical and easy to maintain/replace.',
];

// ── Per-field review mark ────────────────────────────────────────────────────
function ReviewMark({ fieldId, reviewedItems, onToggle }) {
  const done = (reviewedItems || []).includes(fieldId);
  return (
    <button
      onClick={() => onToggle(fieldId)}
      title={done ? 'Reviewed — click to undo' : 'Mark as reviewed'}
      style={{
        flexShrink: 0, width: 18, height: 18, borderRadius: '50%',
        border: `2px solid ${done ? '#22c55e' : '#d1d5db'}`,
        background: done ? '#22c55e' : 'transparent',
        cursor: 'pointer', display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: 0, marginTop: 3,
      }}
    >
      {done && <span style={{ color: '#fff', fontSize: 9, fontWeight: 700, lineHeight: 1 }}>✓</span>}
    </button>
  );
}

// ── Contract Checklist ──────────────────────────────────────────────────────
const REVIEW_FIELD_IDS_BASE = [
  'f_docType','f_status','f_areaVersion','f_feeOffer',
  'f_projectType','f_location','f_customText','f_briefPoints',
  'f_timelines','f_excluded',
  'f_areaUnit','f_offerValidity','f_designPhaseMax','f_execPhaseMax',
  'f_gracePeriod','f_maxTimeline','f_curtailment',
  'f_ccFee','f_perDiem','f_feeGroupings','f_feeNotes','f_feeStages',
  'f_invoicingName','f_invoicingAddress',
];
const REVIEW_FIELD_LABELS = {
  f_docType: 'Doc type', f_status: 'Status', f_areaVersion: 'Area version',
  f_feeOffer: 'Fee offer', f_projectType: 'Project type', f_location: 'Location',
  f_customText: 'Description', f_briefPoints: 'Brief points',
  f_timelines: 'Timelines', f_excluded: 'Excl. services',
  f_areaUnit: 'Area unit', f_offerValidity: 'Offer validity', f_designPhaseMax: 'Design phase max',
  f_execPhaseMax: 'Exec phase max', f_gracePeriod: 'Grace period',
  f_maxTimeline: 'Max timeline', f_curtailment: 'Curtailment %',
  f_ccFee: 'CC fee', f_perDiem: 'Per diem', f_feeGroupings: 'Custom fee lines', f_feeNotes: 'Fee notes',
  f_feeStages: 'Fee stages',
  f_invoicingName: 'Invoicing name', f_invoicingAddress: 'Invoicing address',
  f_sigName: 'Sig. name', f_sigTitle: 'Sig. title', f_sigDin: 'DIN',
};

function isContractFullyReviewed(contract) {
  const isAppt = contract.docType === 'appointment_letter';
  const fieldIds = [...REVIEW_FIELD_IDS_BASE, ...(isAppt ? ['f_sigName', 'f_sigTitle', 'f_sigDin'] : [])];
  const reviewedItems = contract.contractSettings?.reviewedItems || [];
  return fieldIds.every(id => reviewedItems.includes(id));
}

function ContractChecklist({ cs, isAppt }) {
  const reviewedItems = cs.reviewedItems || [];
  const fieldIds = [...REVIEW_FIELD_IDS_BASE, ...(isAppt ? ['f_sigName','f_sigTitle','f_sigDin'] : [])];
  const done = fieldIds.filter(id => reviewedItems.includes(id)).length;
  const total = fieldIds.length;
  const allDone = done === total;

  return (
    <BrioCard style={{ borderLeft: `3px solid ${allDone ? '#22c55e' : '#f59e0b'}` }}>
      <div className="flex items-center justify-between mb-2">
        <h3 style={{ fontWeight: 600 }}>Review Progress</h3>
        <span style={{
          fontSize: 'var(--brio-font-size-s)', fontWeight: 600,
          color: allDone ? '#16a34a' : '#b45309',
          background: allDone ? '#f0fdf4' : '#fffbeb',
          padding: '2px 8px', borderRadius: 999,
        }}>{done}/{total} reviewed</span>
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        {fieldIds.map(id => {
          const isDone = reviewedItems.includes(id);
          return (
            <span key={id} style={{
              fontSize: 'var(--brio-font-size-xs)', padding: '2px 7px', borderRadius: 999,
              background: isDone ? '#dcfce7' : '#f3f4f6',
              color: isDone ? '#16a34a' : '#9ca3af', fontWeight: isDone ? 600 : 400,
            }}>
              {isDone ? '✓ ' : '○ '}{REVIEW_FIELD_LABELS[id]}
            </span>
          );
        })}
      </div>
      {allDone && (
        <div style={{ marginTop: 8, fontSize: 'var(--brio-font-size-s)', color: '#16a34a', fontWeight: 600 }}>
          ✓ All fields reviewed — PDF generation unlocked
        </div>
      )}
    </BrioCard>
  );
}

// ── Table-layout shared helpers ───────────────────────────────────────────────
function AutoTA({ value, onChange, placeholder, style }) {
  const ref = React.useRef(null);
  React.useLayoutEffect(() => {
    if (!ref.current) return;
    ref.current.style.height = 'auto';
    ref.current.style.height = ref.current.scrollHeight + 'px';
  }, [value]);
  return (
    <textarea
      ref={ref}
      className="brio-input"
      style={{ width: '100%', display: 'block', resize: 'none', overflow: 'hidden', minHeight: 32, boxSizing: 'border-box', ...style }}
      value={value || ''}
      onChange={onChange}
      placeholder={placeholder}
    />
  );
}

function TblSection({ label, colSpan = 3 }) {
  return (
    <tr>
      <td colSpan={colSpan} style={{
        padding: '6px 10px',
        fontWeight: 700,
        fontSize: 'var(--brio-font-size-xs)',
        letterSpacing: '0.07em',
        textTransform: 'uppercase',
        color: 'var(--brio-text-muted)',
        background: 'var(--brio-surface)',
        borderTop: '1px solid var(--brio-border)',
        borderBottom: '1px solid var(--brio-border)',
      }}>
        {label}
      </td>
    </tr>
  );
}

function TblRow({ id, label, reviewedItems, onToggle, children, valign = 'middle' }) {
  return (
    <tr style={{ borderBottom: '1px solid var(--brio-border)' }}>
      <td style={{ width: 26, padding: '5px 0 5px 8px', verticalAlign: valign }}>
        {id && <ReviewMark fieldId={id} reviewedItems={reviewedItems} onToggle={onToggle} />}
      </td>
      <td style={{ width: 154, padding: '5px 12px 5px 2px', fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)', verticalAlign: valign, whiteSpace: 'nowrap' }}>
        {label}
      </td>
      <td style={{ padding: '4px 10px 4px 0', verticalAlign: valign }}>
        {children}
      </td>
    </tr>
  );
}

// Shared sidebar used by Layout C and D (identical to Layout B)
function OfferSidebar({ contract, inquiry, reviewedItems, gateIds, allReviewed, reviewedCount, isAppt, onPreview, onDownload, exporting }) {
  const sidebarSections = [
    { label: 'Document', ids: ['f_docType', 'f_status'] },
    { label: 'Brief', ids: ['f_projectType', 'f_location', 'f_customText', 'f_briefPoints'] },
    { label: 'Fees', ids: ['f_areaVersion', 'f_feeOffer', 'f_areaUnit', 'f_ccFee', 'f_perDiem', 'f_feeGroupings', 'f_feeNotes'] },
    { label: 'Schedule', ids: ['f_offerValidity', 'f_designPhaseMax', 'f_execPhaseMax', 'f_gracePeriod', 'f_maxTimeline', 'f_curtailment', 'f_timelines'] },
    { label: 'Services', ids: ['f_excluded'] },
    { label: 'Stages', ids: ['f_feeStages'] },
    { label: 'Invoicing', ids: ['f_invoicingName', 'f_invoicingAddress'] },
    ...(isAppt ? [{ label: 'Signatory', ids: ['f_clientAddress', 'f_sigName', 'f_sigTitle', 'f_sigDin'] }] : []),
  ];
  return (
    <div style={{ width: 224, flexShrink: 0, position: 'sticky', top: 16, maxHeight: 'calc(100vh - 130px)', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ background: 'var(--brio-surface)', border: '1px solid var(--brio-border)', borderRadius: 8, padding: '12px 14px' }}>
        {inquiry ? (
          <>
            <div style={{ fontWeight: 700, fontSize: 'var(--brio-font-size-base)', lineHeight: 1.3, marginBottom: 4 }}>{inquiry.projectName || '—'}</div>
            <div style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)' }}>{inquiry.invoicingName || inquiry.clientNameOverride || inquiry.clientName || '—'}</div>
            {inquiry.locationCity && <div style={{ fontSize: 'var(--brio-font-size-xs)', color: 'var(--brio-text-muted)', marginTop: 2 }}>{inquiry.locationCity}</div>}
          </>
        ) : (
          <div style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)' }}>No inquiry linked</div>
        )}
        <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <BrioBadge variant={STATUS_VARIANT[contract.status] || 'neutral'}>{contract.status}</BrioBadge>
          <BrioBadge variant="neutral">{OFFICE_LABEL[contract.office] || contract.office}</BrioBadge>
          <BrioBadge variant="neutral">{contract.docType === 'appointment_letter' ? 'Appt. Letter' : 'Offer'}</BrioBadge>
        </div>
      </div>
      <div style={{ background: 'var(--brio-surface)', border: `1px solid ${allReviewed ? '#bbf7d0' : 'var(--brio-border)'}`, borderRadius: 8, padding: '12px 14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontWeight: 600, fontSize: 'var(--brio-font-size-s)' }}>Review</span>
          <span style={{ fontSize: 'var(--brio-font-size-s)', color: allReviewed ? '#16a34a' : '#b45309', fontWeight: 600 }}>{reviewedCount}/{gateIds.length}</span>
        </div>
        <div style={{ height: 4, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden', marginBottom: 10 }}>
          <div style={{ width: `${(reviewedCount / gateIds.length) * 100}%`, height: '100%', background: allReviewed ? '#22c55e' : '#f59e0b', transition: 'width 0.2s', borderRadius: 3 }} />
        </div>
        {sidebarSections.map(section => {
          const ids = section.ids.filter(id => gateIds.includes(id));
          const done = ids.filter(id => reviewedItems.includes(id)).length;
          const all = done === ids.length;
          return (
            <div key={section.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--brio-font-size-xs)', marginBottom: 4 }}>
              <span style={{ color: all ? '#16a34a' : 'var(--brio-text-muted)' }}>{all ? '✓' : '○'} {section.label}</span>
              <span style={{ color: all ? '#16a34a' : 'var(--brio-text-muted)', fontWeight: all ? 600 : 400 }}>{done}/{ids.length}</span>
            </div>
          );
        })}
        {allReviewed && <div style={{ marginTop: 8, fontSize: 'var(--brio-font-size-xs)', color: '#16a34a', fontWeight: 600 }}>✓ All fields reviewed</div>}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <BrioButton variant="secondary" onClick={onPreview} disabled={!allReviewed} style={{ width: '100%', justifyContent: 'center' }}>
          <Eye size={13} /> Preview PDF
        </BrioButton>
        <BrioButton variant="primary" onClick={onDownload} disabled={exporting || !allReviewed} style={{ width: '100%', justifyContent: 'center' }}>
          <Download size={13} /> {exporting ? 'Generating…' : 'Download PDF'}
        </BrioButton>
        {!allReviewed && <p style={{ fontSize: 'var(--brio-font-size-xs)', color: '#b45309', textAlign: 'center', margin: 0, lineHeight: 1.4 }}>Complete review marks to unlock PDF</p>}
      </div>
    </div>
  );
}

// ============================================================================
// LAYOUT PROPOSAL C — Sidebar + Single Compact Table
// All fields in one continuous table. Section headers separate the groups.
// Much denser than cards: a field row is ~32px vs a card's ~80px overhead.
// ============================================================================
function OfferTabLayoutC({ contract, inquiry, onField, onInquiryField, onPreview, onDownload, exporting, onNavigateScope }) {
  const currency = contract.office === 'rotterdam' ? 'EUR' : 'INR';
  const isNL = contract.office === 'rotterdam';
  const isAppt = contract.docType === 'appointment_letter';
  const areaStatements = inquiry?.areaStatements || [];
  const feeOffers = inquiry?.feeOffers || [];
  const excluded = contract.excludedServices || [];
  // Office-appropriate defaults for Schedule fields (NL: 6/24/1/33, IN: 9/36/3/48)
  const D_DESIGN = isNL ? 6 : 9;
  const D_EXEC = isNL ? 24 : 36;
  const D_GRACE = isNL ? 1 : 3;
  const D_MAX = isNL ? 33 : 48;
  const defaultPerDiem = contract.office === 'rotterdam' ? 'EUR 350' : 'Rs. 36,000';

  const [localCs, setLocalCs] = useState(() => contract.contractSettings || {});
  // Only sync from DB when switching contracts — not on every save round-trip,
  // which would reset in-progress text entry.
  React.useEffect(() => { setLocalCs(contract.contractSettings || {}); }, [contract.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const cs = localCs;

  // Debounce timers for text fields (avoids DB save on every keystroke)
  const csTimerRef = React.useRef(null);
  const onCs = (key, value) => {
    const next = { ...cs, [key]: value };
    setLocalCs(next);
    clearTimeout(csTimerRef.current);
    csTimerRef.current = setTimeout(() => onField('contractSettings', next), 600);
  };

  // Local state for direct contract text fields to prevent DB round-trips from
  // resetting the cursor position / in-progress text.
  const fieldTimers = React.useRef({});
  const [localFields, setLocalFields] = useState({
    designBriefProjectType: contract.designBriefProjectType || '',
    designBriefLocation: contract.designBriefLocation || '',
    designBriefCustomText: contract.designBriefCustomText || '',
    expensesText: contract.expensesText || '',
    signatoryName: contract.signatoryName || '',
    signatoryTitle: contract.signatoryTitle || '',
    signatoryDin: contract.signatoryDin || '',
  });
  React.useEffect(() => {
    setLocalFields({
      designBriefProjectType: contract.designBriefProjectType || '',
      designBriefLocation: contract.designBriefLocation || '',
      designBriefCustomText: contract.designBriefCustomText || '',
      expensesText: contract.expensesText || '',
      signatoryName: contract.signatoryName || '',
      signatoryTitle: contract.signatoryTitle || '',
      signatoryDin: contract.signatoryDin || '',
    });
  }, [contract.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const onLocalField = (field, value) => {
    setLocalFields(f => ({ ...f, [field]: value }));
    clearTimeout(fieldTimers.current[field]);
    fieldTimers.current[field] = setTimeout(() => onField(field, value), 600);
  };
  const reviewedItems = cs.reviewedItems || [];
  const toggleReview = (id) => { onCs('reviewedItems', reviewedItems.includes(id) ? reviewedItems.filter(x => x !== id) : [...reviewedItems, id]); };

  const toggleService = (svc) => {
    if (svc === PMC_PARENT) {
      const on = excluded.includes(PMC_PARENT);
      if (on) onField('excludedServices', excluded.filter(s => s !== PMC_PARENT && !PMC_SUBITEMS.includes(s)));
      else onField('excludedServices', [...excluded.filter(s => !PMC_SUBITEMS.includes(s)), PMC_PARENT, ...PMC_SUBITEMS]);
    } else {
      onField('excludedServices', excluded.includes(svc) ? excluded.filter(s => s !== svc) : [...excluded, svc]);
    }
  };

  const offerForGroupings = contract.feeOfferIndex != null ? feeOffers[contract.feeOfferIndex] : null;
  const offerGroupIdSet = offerForGroupings?.groupRates ? new Set(Object.keys(offerForGroupings.groupRates)) : new Set();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const areaVersionForGroupings = React.useMemo(() => {
    if (offerGroupIdSet.size > 0) { const found = areaStatements.find(a => (a.groups || []).some(g => offerGroupIdSet.has(g.id))); if (found) return found; }
    return areaStatements.length > 0 ? areaStatements[areaStatements.length - 1] : null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaStatements, contract.feeOfferIndex]);
  const areaGroupsForFeeConfig = (areaVersionForGroupings?.groups || []).map(g => ({ id: g.id || '', label: g.label || g.name || '' }));
  const feeLineGroupings = cs.feeLineGroupings || [];
  const usedGroupIdsInGroupings = new Set(feeLineGroupings.flatMap(gl => gl.groupIds || []));
  const addFeeGrouping = () => onCs('feeLineGroupings', [...feeLineGroupings, { id: `flg_${Date.now()}`, label: '', groupIds: [], rate: '' }]);
  const updateFeeGrouping = (idx, key, value) => onCs('feeLineGroupings', feeLineGroupings.map((gl, i) => i === idx ? { ...gl, [key]: value } : gl));
  const toggleFeeGroupingGroup = (glIdx, groupId) => { const gl = feeLineGroupings[glIdx]; updateFeeGrouping(glIdx, 'groupIds', (gl.groupIds || []).includes(groupId) ? gl.groupIds.filter(id => id !== groupId) : [...(gl.groupIds || []), groupId]); };
  const removeFeeGrouping = (idx) => onCs('feeLineGroupings', feeLineGroupings.filter((_, i) => i !== idx));

  const designBriefOptionalPoints = cs.designBriefOptionalPoints ?? [0, 1, 2, 3];
  const toggleOptionalPoint = (idx) => { const cur = cs.designBriefOptionalPoints ?? [0, 1, 2, 3]; onCs('designBriefOptionalPoints', cur.includes(idx) ? cur.filter(i => i !== idx) : [...cur, idx].sort()); };
  const includeCurtailment = cs.includeCurtailment ?? true;
  const includeTimelines = cs.includeTimelines ?? true;
  const timelineStages = cs.timelineStages || [];
  const timelineTableRef = React.useRef(null);
  useArrowTableNav(timelineTableRef);
  const defaultCommentForStage = (name) => { const n = (name || '').toLowerCase(); if (n.includes('interior') && (n.includes('working') || n.includes('gfc') || n.includes('construction doc'))) return 'overlaps with the construction period'; if (n.includes('gfc') || n.includes('working drawings') || n.includes('construction doc') || n.includes('construction admin') || n.includes('construction coord') || n.includes(' cc ') || n.endsWith(' cc')) return 'as per construction schedule'; return ''; };

  // Auto-populate timeline stages when contract loads and timeline is enabled but empty.
  React.useEffect(() => {
    if (!includeTimelines || timelineStages.length > 0) return;
    const rawScope = (contract.feeStageOverrides || []).filter(s => !isAdvanceStage(s.stageName || s.name || ''));
    const nonCT = rawScope.filter(s => !isCTStage(s.stageName || s.name || ''));
    const nonCTRows = nonCT.map(s => { const name = s.stageName || s.name || ''; return { name, duration: '', comment: defaultCommentForStage(name) }; });
    if (nonCTRows.length === 0) return; // no scope set yet — don't auto-populate with just CT
    const ctRow = { name: 'Construction Time', duration: '', comment: 'as per construction schedule' };
    const completionIdx = nonCTRows.findIndex(s => s.name.toLowerCase().includes('completion'));
    const insertAt = completionIdx >= 0 ? completionIdx : nonCTRows.length;
    const auto = [...nonCTRows.slice(0, insertAt), ctRow, ...nonCTRows.slice(insertAt)];
    onCs('timelineStages', auto);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract.id]);

  const gateIds = [...REVIEW_FIELD_IDS_BASE, 'f_feeStages', ...(isAppt ? ['f_clientAddress', 'f_sigName', 'f_sigTitle', 'f_sigDin'] : [])];
  const allReviewed = gateIds.every(id => reviewedItems.includes(id));
  const reviewedCount = gateIds.filter(id => reviewedItems.includes(id)).length;

  const rm = (id) => ({ id, reviewedItems, onToggle: toggleReview });

  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      <OfferSidebar contract={contract} inquiry={inquiry} reviewedItems={reviewedItems} gateIds={gateIds} allReviewed={allReviewed} reviewedCount={reviewedCount} isAppt={isAppt} onPreview={onPreview} onDownload={onDownload} exporting={exporting} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ border: '1px solid var(--brio-border)', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>

              {/* ── Contract & Brief ── */}
              <TblSection label="Contract & Brief" />
              <TblRow {...rm('f_docType')} label="Document type">
                <BrioSelect style={{ fontSize: 'var(--brio-font-size-s)' }} value={contract.docType} onChange={e => onField('docType', e.target.value)}>
                  <option value="offer">Consultancy Offer</option>
                  <option value="appointment_letter">Appointment Letter</option>
                </BrioSelect>
              </TblRow>
              <TblRow {...rm('f_status')} label="Status">
                <BrioSelect style={{ fontSize: 'var(--brio-font-size-s)' }} value={contract.status} onChange={e => onField('status', e.target.value)}>
                  <option value="draft">Draft</option>
                  <option value="sent">Sent</option>
                  <option value="confirmed">Confirmed</option>
                </BrioSelect>
              </TblRow>
              <TblRow label="Office">
                <BrioSelect style={{ fontSize: 'var(--brio-font-size-s)' }} value={contract.office || 'mumbai'} onChange={e => onField('office', e.target.value)}>
                  <option value="mumbai">IN — Mumbai</option>
                  <option value="rotterdam">NL — Rotterdam</option>
                </BrioSelect>
              </TblRow>
              <TblRow {...rm('f_projectType')} label="Project type">
                <AutoTA value={localFields.designBriefProjectType} onChange={e => onLocalField('designBriefProjectType', e.target.value)} style={{ fontSize: 'var(--brio-font-size-s)' }} />
              </TblRow>
              <TblRow {...rm('f_location')} label="Location">
                <AutoTA value={localFields.designBriefLocation} onChange={e => onLocalField('designBriefLocation', e.target.value)} style={{ fontSize: 'var(--brio-font-size-s)' }} />
              </TblRow>
              <TblRow {...rm('f_customText')} label="Description">
                <AutoTA value={localFields.designBriefCustomText} onChange={e => onLocalField('designBriefCustomText', e.target.value)} placeholder="Appears after standard paragraphs" style={{ minHeight: 56, fontSize: 'var(--brio-font-size-s)' }} />
              </TblRow>
              <TblRow {...rm('f_briefPoints')} label="Brief points" valign="top">
                <div className="flex flex-col gap-1" style={{ paddingTop: 4, paddingBottom: 4 }}>
                  {(contract.office === 'rotterdam' ? NL_OPTIONAL_BRIEF_POINTS : OPTIONAL_BRIEF_POINTS).map((pt, idx) => (
                    <label key={idx} className="flex items-start gap-2" style={{ fontSize: 'var(--brio-font-size-s)' }}>
                      <input type="checkbox" className="brio-checkbox" style={{ marginTop: 2, flexShrink: 0 }} checked={designBriefOptionalPoints.includes(idx)} onChange={() => toggleOptionalPoint(idx)} />
                      <span style={{ color: designBriefOptionalPoints.includes(idx) ? 'inherit' : 'var(--brio-text-muted)' }}>{pt}</span>
                    </label>
                  ))}
                </div>
                {(cs.designBriefCustomPoints || []).map((pt, idx) => {
                  const isObj = pt && typeof pt === 'object';
                  const text = isObj ? (pt.text || '') : (pt || '');
                  const enabled = isObj ? (pt.enabled !== false) : true;
                  return (
                    <div key={idx} className="flex gap-2 items-start" style={{ marginTop: 4 }}>
                      <input type="checkbox" className="brio-checkbox" style={{ marginTop: 3, flexShrink: 0 }} checked={enabled} onChange={() => { const next = (cs.designBriefCustomPoints || []).map((p, i) => { if (i !== idx) return p; const o = (p && typeof p === 'object') ? p : { text: p || '', enabled: true }; return { ...o, enabled: !o.enabled }; }); onCs('designBriefCustomPoints', next); }} />
                      <AutoTA value={text} onChange={e => { const next = (cs.designBriefCustomPoints || []).map((p, i) => { if (i !== idx) return p; const o = (p && typeof p === 'object') ? p : { text: p || '', enabled: true }; return { ...o, text: e.target.value }; }); onCs('designBriefCustomPoints', next); }} placeholder="Custom brief point…" style={{ flex: 1, fontSize: 'var(--brio-font-size-s)', opacity: enabled ? 1 : 0.5 }} />
                      <BrioButton variant="secondary" onClick={() => onCs('designBriefCustomPoints', (cs.designBriefCustomPoints || []).filter((_, i) => i !== idx))}><Trash2 size={12} /></BrioButton>
                    </div>
                  );
                })}
                <BrioButton variant="secondary" style={{ marginTop: 4 }} onClick={() => onCs('designBriefCustomPoints', [...(cs.designBriefCustomPoints || []), { text: '', enabled: true }])}><Plus size={12} /> Add custom point</BrioButton>
              </TblRow>

              {/* ── Area & Fees ── */}
              <TblSection label="Area & Fees" />
              <TblRow {...rm('f_areaVersion')} label="Area statement">
                <BrioSelect style={{ fontSize: 'var(--brio-font-size-s)' }} value={contract.areaStatementVersionId || ''} onChange={e => onField('areaStatementVersionId', e.target.value)}>
                  <option value="">Select version…</option>
                  {areaStatements.map((a, i) => <option key={a.id} value={a.id}>{a.name || a.label || `Version ${i + 1}`}{a.createdAt ? ` — ${new Date(a.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}</option>)}
                </BrioSelect>
              </TblRow>
              <TblRow {...rm('f_feeOffer')} label="Fee offer">
                <BrioSelect style={{ fontSize: 'var(--brio-font-size-s)' }} value={contract.feeOfferIndex != null ? String(contract.feeOfferIndex) : ''} onChange={e => onField('feeOfferIndex', e.target.value === '' ? null : Number(e.target.value))}>
                  <option value="">Select offer…</option>
                  {feeOffers.map((o, idx) => <option key={idx} value={idx}>{(o.label || `Offer ${idx + 1}`) + (o.date ? ` (${o.date})` : '')}</option>)}
                </BrioSelect>
              </TblRow>
              <TblRow {...rm('f_areaUnit')} label="Area unit">
                <div className="flex gap-2">
                  {[{ v: 'sqft', l: 'ft²' }, { v: 'sqm', l: 'm²' }].map(opt => (
                    <button key={opt.v} onClick={() => onCs('areaUnit', opt.v)} style={{ padding: '3px 12px', borderRadius: 4, border: '1px solid', fontSize: 'var(--brio-font-size-s)', cursor: 'pointer', background: (cs.areaUnit || 'sqft') === opt.v ? 'var(--brio-gold)' : 'transparent', borderColor: (cs.areaUnit || 'sqft') === opt.v ? 'var(--brio-gold)' : 'var(--brio-border)', color: (cs.areaUnit || 'sqft') === opt.v ? '#fff' : 'inherit', fontWeight: (cs.areaUnit || 'sqft') === opt.v ? 600 : 400 }}>{opt.l}</button>
                  ))}
                </div>
              </TblRow>
              <TblRow {...rm('f_ccFee')} label={`CC fee (${currency})`}>
                <BrioInput type="number" value={contract.constructionCoordFee ?? ''} onChange={e => onField('constructionCoordFee', e.target.value === '' ? null : Number(e.target.value))} style={{ maxWidth: 160, fontSize: 'var(--brio-font-size-s)' }} />
              </TblRow>
              <TblRow {...rm('f_perDiem')} label={`Per diem (${currency})`}>
                <AutoTA value={localFields.expensesText} onChange={e => onLocalField('expensesText', e.target.value)} placeholder={defaultPerDiem} style={{ fontSize: 'var(--brio-font-size-s)' }} />
              </TblRow>
              {areaGroupsForFeeConfig.length > 0 && offerForGroupings?.groupRates && (
                <TblRow {...rm('f_feeGroupings')} label="Custom fee lines">
                  <div>
                    {feeLineGroupings.map((gl, glIdx) => (
                      <div key={gl.id} style={{ border: '1px solid var(--brio-border)', borderRadius: 6, padding: '6px 8px', marginBottom: 6 }}>
                        <div className="flex gap-2 items-center mb-1">
                          <BrioInput style={{ flex: 1, fontSize: 'var(--brio-font-size-s)' }} placeholder="Fee line label" value={gl.label || ''} onChange={e => updateFeeGrouping(glIdx, 'label', e.target.value)} />
                          <BrioInput type="number" style={{ width: 130, fontSize: 'var(--brio-font-size-s)' }} placeholder={`${currency}/${(cs.areaUnit || 'sqft') === 'sqm' ? 'm²' : 'ft²'}`} value={gl.rate || ''} onChange={e => updateFeeGrouping(glIdx, 'rate', e.target.value)} />
                          <BrioButton variant="secondary" onClick={() => removeFeeGrouping(glIdx)}><Trash2 size={12} /></BrioButton>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {areaGroupsForFeeConfig.map(g => { const checked = (gl.groupIds || []).includes(g.id); const disabled = !checked && usedGroupIdsInGroupings.has(g.id); return (<label key={g.id} className="flex items-center gap-1" style={{ fontSize: 'var(--brio-font-size-s)', opacity: disabled ? 0.4 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}><input type="checkbox" className="brio-checkbox" checked={checked} disabled={disabled} onChange={() => !disabled && toggleFeeGroupingGroup(glIdx, g.id)} />{g.label}</label>); })}
                        </div>
                      </div>
                    ))}
                    <BrioButton variant="secondary" onClick={addFeeGrouping}><Plus size={12} /> Add grouping</BrioButton>
                  </div>
                </TblRow>
              )}
              <TblRow {...rm('f_feeNotes')} label="Fee notes" valign="top">
                <div className="space-y-1" style={{ paddingTop: 3, paddingBottom: 3 }}>
                  {(cs.feeNotes || DEFAULT_FEE_NOTES).map((note, idx) => (
                    <div key={note.id || idx} className="flex gap-2 items-start">
                      <input type="checkbox" className="brio-checkbox" style={{ marginTop: 3, flexShrink: 0 }} checked={note.enabled !== false} onChange={() => { const cur = cs.feeNotes || DEFAULT_FEE_NOTES.map(n => ({ ...n })); onCs('feeNotes', cur.map((n, i) => i === idx ? { ...n, enabled: !n.enabled } : n)); }} />
                      {note.text === '__area__' ? (
                        <span style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)', fontStyle: 'italic' }}>Area breakdown — auto-computed</span>
                      ) : (
                        <AutoTA value={note.text} onChange={e => { const cur = cs.feeNotes || DEFAULT_FEE_NOTES.map(n => ({ ...n })); onCs('feeNotes', cur.map((n, i) => i === idx ? { ...n, text: e.target.value } : n)); }} style={{ flex: 1, opacity: note.enabled === false ? 0.5 : 1, fontSize: 'var(--brio-font-size-s)' }} />
                      )}
                      {!['n1', 'n2_area', 'n3', 'n4', 'n5'].includes(note.id) && (
                        <BrioButton variant="secondary" onClick={() => { const cur = cs.feeNotes || DEFAULT_FEE_NOTES.map(n => ({ ...n })); onCs('feeNotes', cur.filter((_, i) => i !== idx)); }}><Trash2 size={12} /></BrioButton>
                      )}
                    </div>
                  ))}
                  <BrioButton variant="secondary" style={{ marginTop: 4 }} onClick={() => { const cur = cs.feeNotes || DEFAULT_FEE_NOTES.map(n => ({ ...n })); onCs('feeNotes', [...cur, { id: `custom_${Date.now()}`, text: '', enabled: true }]); }}><Plus size={12} /> Add note</BrioButton>
                </div>
              </TblRow>

              {/* ── Stage Proportions (payment schedule) ── */}
              <TblSection label="Stage Proportions · Payment Schedule" />
              <tr style={{ borderBottom: '1px solid var(--brio-border)' }}>
                <td style={{ width: 26, padding: '8px 0 8px 8px', verticalAlign: 'top' }}>
                  <ReviewMark fieldId="f_feeStages" reviewedItems={reviewedItems} onToggle={toggleReview} />
                </td>
                <td style={{ width: 154, padding: '8px 12px 8px 2px', fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)', verticalAlign: 'top' }}>Defined in Scope tab</td>
                <td style={{ padding: '8px 10px 8px 0' }}>
                  {(contract.feeStageOverrides || []).length === 0 ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 'var(--brio-font-size-s)', color: '#b45309' }}>No stages set yet</span>
                      <button style={{ fontSize: 'var(--brio-font-size-xs)', color: 'var(--brio-gold)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }} onClick={onNavigateScope}>Go to Scope of Work →</button>
                    </div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ fontSize: 'var(--brio-font-size-xs)', color: 'var(--brio-text-muted)', fontWeight: 600 }}>
                          <td style={{ paddingBottom: 3, paddingRight: 12 }}>Stage</td>
                          <td style={{ paddingBottom: 3, paddingRight: 12, textAlign: 'right' }}>%</td>
                          <td style={{ paddingBottom: 3, textAlign: 'right' }}>Cumulative</td>
                        </tr>
                      </thead>
                      <tbody>
                        {(contract.feeStageOverrides || []).map((s, idx) => (
                          <tr key={idx} style={{ fontSize: 'var(--brio-font-size-s)', borderTop: '1px solid var(--brio-border)' }}>
                            <td style={{ padding: '3px 12px 3px 0' }}>{s.stageName || `Stage ${idx + 1}`}</td>
                            <td style={{ padding: '3px 12px 3px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{s.proportion || '—'}</td>
                            <td style={{ padding: '3px 0', textAlign: 'right', color: 'var(--brio-text-muted)', fontVariantNumeric: 'tabular-nums' }}>{s.cumulative || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </td>
              </tr>

              {/* ── Schedule & Validity ── */}
              <TblSection label="Schedule & Validity" />
              <TblRow {...rm('f_offerValidity')} label="Offer validity (months)"><BrioInput type="number" min="0" value={contract.validityMonths ?? 2} onChange={e => onField('validityMonths', e.target.value === '' ? 2 : Number(e.target.value))} style={{ maxWidth: 100, fontSize: 'var(--brio-font-size-s)' }} /></TblRow>
              <TblRow {...rm('f_designPhaseMax')} label="Design phase max (months)"><BrioInput type="number" min="0" value={cs.designValidityMonths ?? D_DESIGN} onChange={e => onCs('designValidityMonths', e.target.value === '' ? D_DESIGN : Number(e.target.value))} style={{ maxWidth: 100, fontSize: 'var(--brio-font-size-s)' }} /></TblRow>
              <TblRow {...rm('f_execPhaseMax')} label="Execution phase max (months)"><BrioInput type="number" min="0" value={cs.executionValidityMonths ?? D_EXEC} onChange={e => onCs('executionValidityMonths', e.target.value === '' ? D_EXEC : Number(e.target.value))} style={{ maxWidth: 100, fontSize: 'var(--brio-font-size-s)' }} /></TblRow>
              <TblRow {...rm('f_gracePeriod')} label="Grace period (months)"><BrioInput type="number" min="0" value={cs.gracePeriodMonths ?? D_GRACE} onChange={e => onCs('gracePeriodMonths', e.target.value === '' ? D_GRACE : Number(e.target.value))} style={{ maxWidth: 100, fontSize: 'var(--brio-font-size-s)' }} /></TblRow>
              <TblRow {...rm('f_maxTimeline')} label="Max project timeline (months)"><BrioInput type="number" min="0" value={cs.maxTimelineMonths ?? D_MAX} onChange={e => onCs('maxTimelineMonths', e.target.value === '' ? D_MAX : Number(e.target.value))} style={{ maxWidth: 100, fontSize: 'var(--brio-font-size-s)' }} /></TblRow>
              <TblRow {...rm('f_curtailment')} label="Curtailment % (after Stage Schematic Design)">
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2" style={{ fontSize: 'var(--brio-font-size-s)', whiteSpace: 'nowrap' }}>
                    <input type="checkbox" className="brio-checkbox" checked={includeCurtailment} onChange={e => onCs('includeCurtailment', e.target.checked)} />
                    <span>Include clause</span>
                  </label>
                  {includeCurtailment && (
                    <BrioInput type="number" min="0" max="100" value={cs.curtailmentPct ?? 10} onChange={e => onCs('curtailmentPct', e.target.value === '' ? 10 : Number(e.target.value))} style={{ maxWidth: 100, fontSize: 'var(--brio-font-size-s)' }} />
                  )}
                </div>
              </TblRow>
              <TblRow {...rm('f_timelines')} label="Timeline chapter" valign="top">
                <div>
                  <label className="flex items-center gap-2" style={{ fontSize: 'var(--brio-font-size-s)' }}>
                    <input type="checkbox" className="brio-checkbox" checked={includeTimelines} onChange={e => {
                      onCs('includeTimelines', e.target.checked);
                      // Auto-populate timeline stages from payment scope when first enabling
                      if (e.target.checked && (cs.timelineStages || []).length === 0) {
                        const rawScope = (contract.feeStageOverrides || []).filter(s => !isAdvanceStage(s.stageName || s.name || ''));
                        const nonCT = rawScope.filter(s => !isCTStage(s.stageName || s.name || ''));
                        const nonCTRows = nonCT.map(s => { const name = s.stageName || s.name || ''; return { name, duration: '', comment: defaultCommentForStage(name) }; });
                        const ctRow = { name: 'Construction Time', duration: '', comment: 'as per construction schedule' };
                        const completionIdx = nonCTRows.findIndex(s => s.name.toLowerCase().includes('completion'));
                        const insertAt = completionIdx >= 0 ? completionIdx : nonCTRows.length;
                        const auto = [...nonCTRows.slice(0, insertAt), ctRow, ...nonCTRows.slice(insertAt)];
                        if (auto.length > 0) onCs('timelineStages', auto);
                      }
                    }} />
                    <span>Include in PDF</span>
                  </label>
                  {includeTimelines && (
                    <div style={{ marginTop: 8 }}>
                      <div className="flex items-center justify-between mb-1">
                        <span style={{ fontSize: 'var(--brio-font-size-s)', fontWeight: 600 }}>Stages</span>
                        <div className="flex gap-1">
                          <BrioButton variant="secondary" onClick={() => {
                            const rawScope = (contract.feeStageOverrides || [])
                              .filter(s => !isAdvanceStage(s.stageName || s.name || ''));
                            // Collapse all CT stages into one "Construction Time" placed before Completion
                            const nonCT = rawScope.filter(s => !isCTStage(s.stageName || s.name || ''));
                            const nonCTRows = nonCT.map(s => { const name = s.stageName || s.name || ''; return { name, duration: '', comment: defaultCommentForStage(name) }; });
                            // Always insert "Construction Time" just before Completion
                            const ctRow = { name: 'Construction Time', duration: '', comment: 'as per construction schedule' };
                            const completionIdx = nonCTRows.findIndex(s => s.name.toLowerCase().includes('completion'));
                            const insertAt = completionIdx >= 0 ? completionIdx : nonCTRows.length;
                            const fromScope = [
                              ...nonCTRows.slice(0, insertAt),
                              ctRow,
                              ...nonCTRows.slice(insertAt),
                            ];
                            if (fromScope.length > 0) { onCs('timelineStages', fromScope); }
                            else { onNavigateScope?.(); }
                          }}>Load from Scope{(contract.feeStageOverrides || []).length === 0 && ' →'}</BrioButton>
                          <BrioButton variant="secondary" onClick={() => onCs('timelineStages', [...timelineStages, { name: '', duration: '', comment: '' }])}><Plus size={12} /> Add stage</BrioButton>
                        </div>
                      </div>
                      {timelineStages.length > 0 && (
                        <div ref={timelineTableRef}>
                          <div className="grid grid-cols-12 gap-1 mb-1" style={{ fontSize: 'var(--brio-font-size-xs)', color: 'var(--brio-text-muted)', fontWeight: 600 }}>
                            <div className="col-span-5">Stage</div><div className="col-span-2">Months</div><div className="col-span-4">Comment</div><div className="col-span-1"></div>
                          </div>
                          {timelineStages.map((s, idx) => (
                            <div key={idx} className="grid grid-cols-12 gap-1 mb-1 items-center">
                              <BrioInput className="col-span-5" placeholder="Stage name" value={s.name || ''} onChange={e => { const next = timelineStages.map((ts, i) => i === idx ? { ...ts, name: e.target.value } : ts); onCs('timelineStages', next); }} style={{ fontSize: 'var(--brio-font-size-s)' }} />
                              <BrioInput className="col-span-2" placeholder="Mo." value={s.duration || ''} onChange={e => { const next = timelineStages.map((ts, i) => i === idx ? { ...ts, duration: e.target.value } : ts); onCs('timelineStages', next); }} style={{ fontSize: 'var(--brio-font-size-s)' }} />
                              <BrioInput className="col-span-4" placeholder="Comment" value={s.comment || ''} onChange={e => { const next = timelineStages.map((ts, i) => i === idx ? { ...ts, comment: e.target.value } : ts); onCs('timelineStages', next); }} style={{ fontSize: 'var(--brio-font-size-s)' }} />
                              <div className="col-span-1 flex justify-end"><BrioButton variant="secondary" onClick={() => onCs('timelineStages', timelineStages.filter((_, i) => i !== idx))}><Trash2 size={12} /></BrioButton></div>
                            </div>
                          ))}
                          <div className="grid grid-cols-12 gap-1 mt-1 items-center" style={{ fontSize: 'var(--brio-font-size-s)' }}>
                            <div className="col-span-1" style={{ fontWeight: 600 }}>Total</div>
                            <label className="col-span-3 flex items-center gap-1"><span style={{ color: 'var(--brio-text-muted)', whiteSpace: 'nowrap' }}>label:</span><BrioInput placeholder="of design stages" value={cs.timelineTotalLabel ?? 'of design stages'} onChange={e => onCs('timelineTotalLabel', e.target.value)} style={{ flex: 1, fontSize: 'var(--brio-font-size-s)' }} /></label>
                            <BrioInput className="col-span-2" placeholder="Mo." value={cs.timelineTotalDuration ?? ''} onChange={e => onCs('timelineTotalDuration', e.target.value)} style={{ fontSize: 'var(--brio-font-size-s)' }} />
                            <BrioInput className="col-span-6" placeholder="comment (optional)" value={cs.timelineTotalComment ?? ''} onChange={e => onCs('timelineTotalComment', e.target.value)} style={{ fontSize: 'var(--brio-font-size-s)' }} />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </TblRow>

              {/* ── Excluded Services ── */}
              <TblSection label="Excluded Services" />
              <tr style={{ borderBottom: '1px solid var(--brio-border)' }}>
                <td style={{ width: 26, padding: '8px 0 8px 8px', verticalAlign: 'top' }}>
                  <ReviewMark fieldId="f_excluded" reviewedItems={reviewedItems} onToggle={toggleReview} />
                </td>
                <td style={{ width: 154, padding: '8px 12px 8px 2px', fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)', verticalAlign: 'top' }}>Checked = excluded</td>
                <td style={{ padding: '8px 10px 8px 0' }}>
                  <div className="flex flex-col gap-1">
                    {STANDARD_SERVICES.map(svc => (
                      <label key={svc} className="flex items-center gap-2" style={{ fontSize: 'var(--brio-font-size-s)' }}>
                        <input type="checkbox" className="brio-checkbox" checked={excluded.includes(svc)} onChange={() => toggleService(svc)} />
                        <span>{svc}</span>
                      </label>
                    ))}
                  </div>
                  {excluded.includes(PMC_PARENT) && (
                    <div style={{ marginTop: 6, paddingLeft: 20, borderLeft: '2px solid var(--brio-border)' }}>
                      <div style={{ fontSize: 'var(--brio-font-size-xs)', color: 'var(--brio-text-muted)', marginBottom: 3 }}>PMC sub-items:</div>
                      {PMC_SUBITEMS.map(sub => (
                        <label key={sub} className="flex items-center gap-2 mb-1" style={{ fontSize: 'var(--brio-font-size-s)' }}>
                          <input type="checkbox" className="brio-checkbox" checked={excluded.includes(sub)} onChange={() => toggleService(sub)} />
                          <span>{sub}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </td>
              </tr>

              {/* ── Client / Invoicing ── */}
              <TblSection label="Client / Invoicing" />
              <TblRow {...rm('f_invoicingName')} label="Invoicing name">
                <div>
                  <AutoTA value={inquiry?.invoicingName || ''} onChange={e => onInquiryField?.('invoicingName', e.target.value)} placeholder="Legal / invoicing name used on contract…" style={{ fontSize: 'var(--brio-font-size-s)' }} />
                  <span style={{ fontSize: 'var(--brio-font-size-xs)', color: 'var(--brio-text-muted)' }}>Saved to inquiry · copied to client on conversion</span>
                </div>
              </TblRow>
              <TblRow {...rm('f_invoicingAddress')} label="Invoicing address">
                <div>
                  <AutoTA value={inquiry?.invoicingAddress || ''} onChange={e => onInquiryField?.('invoicingAddress', e.target.value)} placeholder="Address used on contracts…" style={{ minHeight: 52, fontSize: 'var(--brio-font-size-s)' }} />
                  <span style={{ fontSize: 'var(--brio-font-size-xs)', color: 'var(--brio-text-muted)' }}>Saved to inquiry · copied to client on conversion</span>
                </div>
              </TblRow>

              {/* ── Signatory (appointment letter only) ── */}
              {isAppt && (
                <>
                  <TblSection label="Signatory" />
                  <TblRow {...rm('f_clientAddress')} label="Client address">
                    <div>
                      <AutoTA value={inquiry?.clientAddressOverride || ''} onChange={e => onInquiryField?.('clientAddressOverride', e.target.value)} placeholder="e.g. 42 Marine Drive, Mumbai 400001" style={{ fontSize: 'var(--brio-font-size-s)' }} />
                      <span style={{ fontSize: 'var(--brio-font-size-xs)', color: 'var(--brio-text-muted)' }}>Saved to linked inquiry</span>
                    </div>
                  </TblRow>
                  <TblRow {...rm('f_sigName')} label="Name"><BrioInput value={localFields.signatoryName} onChange={e => onLocalField('signatoryName', e.target.value)} style={{ fontSize: 'var(--brio-font-size-s)' }} /></TblRow>
                  <TblRow {...rm('f_sigTitle')} label="Title"><BrioInput value={localFields.signatoryTitle} onChange={e => onLocalField('signatoryTitle', e.target.value)} style={{ fontSize: 'var(--brio-font-size-s)' }} /></TblRow>
                  <TblRow {...rm('f_sigDin')} label="DIN"><BrioInput value={localFields.signatoryDin} onChange={e => onLocalField('signatoryDin', e.target.value)} style={{ fontSize: 'var(--brio-font-size-s)' }} /></TblRow>
                </>
              )}

            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// T&C VERSION ROW
// ============================================================================
function TcVersionRow({ tpl, isApplied, onApply, onUpdate, onDelete, onDuplicate, onPreview, onDownload }) {
  const [renaming, setRenaming] = useState(false);
  const [labelDraft, setLabelDraft] = useState(tpl.label);
  const [expanded, setExpanded] = useState(false);
  const [contentDraft, setContentDraft] = useState(tpl.content || '');
  const [saving, setSaving] = useState(false);

  const saveLabel = async () => {
    if (labelDraft.trim() && labelDraft.trim() !== tpl.label) await onUpdate(tpl.id, { label: labelDraft.trim() });
    setRenaming(false);
  };
  const saveContent = async () => {
    setSaving(true);
    await onUpdate(tpl.id, { content: contentDraft });
    setSaving(false);
    setExpanded(false);
  };

  const iconBtn = (onClick, title, children, danger = false) => (
    <button onClick={onClick} title={title} style={{
      background: 'none', border: 'none', cursor: 'pointer', padding: '4px 5px', borderRadius: 4,
      color: danger ? 'var(--brio-red, #c0392b)' : 'var(--brio-text-muted)',
      display: 'inline-flex', alignItems: 'center',
    }}>{children}</button>
  );

  return (
    <div style={{ border: isApplied ? '2px solid var(--brio-gold)' : '1px solid var(--brio-border)', borderRadius: 6, marginBottom: 6, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', background: 'var(--brio-surface)' }}>
        {renaming ? (
          <BrioInput
            value={labelDraft}
            onChange={e => setLabelDraft(e.target.value)}
            onBlur={saveLabel}
            onKeyDown={e => { if (e.key === 'Enter') saveLabel(); if (e.key === 'Escape') { setLabelDraft(tpl.label); setRenaming(false); } }}
            style={{ flex: 1 }}
            autoFocus
          />
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 600, fontSize: 'var(--brio-font-size-base)' }}>{tpl.label}</span>
            {isApplied && <BrioBadge variant="success">Applied to this contract</BrioBadge>}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {onApply && !isApplied && (
            <BrioButton variant="secondary" onClick={() => onApply(tpl.id)}>
              Apply to contract
            </BrioButton>
          )}
          {iconBtn(() => onPreview(tpl.content), 'Preview PDF', <Eye size={14} />)}
          {iconBtn(() => onDownload(tpl.content, tpl.label), 'Download PDF', <Download size={14} />)}
          {iconBtn(() => setRenaming(r => !r), 'Rename', <Pencil size={14} />)}
          {iconBtn(() => { setContentDraft(tpl.content || ''); setExpanded(e => !e); }, expanded ? 'Close editor' : 'Edit content', <FileText size={14} />)}
          {iconBtn(() => onDuplicate(tpl), 'Duplicate', <Copy size={14} />)}
          {iconBtn(() => {
            // eslint-disable-next-line no-restricted-globals, no-alert
            if (window.confirm(`Delete "${tpl.label}"? This cannot be undone.`)) onDelete(tpl.id);
          }, 'Delete', <Trash2 size={14} />, true)}
        </div>
      </div>
      {expanded && (
        <div style={{ padding: 12, borderTop: '1px solid var(--brio-border)' }}>
          <textarea
            className="brio-input"
            style={{ width: '100%', minHeight: 380, fontFamily: 'monospace', fontSize: 11, lineHeight: 1.5 }}
            value={contentDraft}
            onChange={e => setContentDraft(e.target.value)}
          />
          <div className="flex justify-end gap-2 mt-2">
            <BrioButton variant="secondary" onClick={() => setExpanded(false)}>Cancel</BrioButton>
            <BrioButton variant="primary" onClick={saveContent} disabled={saving}>
              <Save size={14} /> {saving ? 'Saving…' : 'Save version'}
            </BrioButton>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// T&C TAB
// ============================================================================
function TcTab({ contract, templates, onField, onPreview, onDownload, exporting, onCreateTemplate, onUpdateTemplate, onDeleteTemplate }) {
  const versions = templates.filter(t => t.office === contract.office || t.office === 'both');
  const [creating, setCreating] = useState(false);
  const standardHtml = buildStandardTcHtml({});

  const appliedId = contract?.tcTemplateId ?? null;

  const handleNewVersion = async () => {
    if (!onCreateTemplate) return;
    setCreating(true);
    const bodyHtml = getStandardTcBodyHtml();
    const label = `Version ${versions.length + 1}`;
    await onCreateTemplate({ label, office: contract.office || 'mumbai', content: bodyHtml });
    setCreating(false);
  };

  const handleDuplicate = async (tpl) => {
    if (!onCreateTemplate) return;
    await onCreateTemplate({ label: `${tpl.label} (copy)`, office: tpl.office, content: tpl.content });
  };

  return (
    <div className="space-y-4">
      {/* Standard base — always shown, read-only */}
      <BrioCard style={{ padding: 0, overflow: 'hidden', border: appliedId === null ? '2px solid var(--brio-gold)' : undefined }}>
        <div style={{ padding: 'var(--brio-space-m)', borderBottom: '1px solid var(--brio-color-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div className="flex items-center gap-2">
            <h3 style={{ fontWeight: 600 }}>Standard Terms &amp; Conditions</h3>
            {appliedId === null && <BrioBadge variant="success">Applied to this contract</BrioBadge>}
          </div>
          <div className="flex gap-2">
            {appliedId !== null && (
              <BrioButton variant="secondary" onClick={() => onField('tcTemplateId', null)}>
                Use standard
              </BrioButton>
            )}
            <BrioButton variant="secondary" onClick={() => onPreview()}>
              <Eye size={14} /> Preview
            </BrioButton>
            <BrioButton variant="primary" onClick={() => onDownload()} disabled={exporting}>
              <Download size={14} /> {exporting ? 'Generating...' : 'Download PDF'}
            </BrioButton>
          </div>
        </div>
        <iframe
          srcDoc={standardHtml}
          title="Standard T&C"
          style={{ width: '100%', height: 600, border: 'none', display: 'block' }}
          sandbox="allow-same-origin"
        />
      </BrioCard>

      {/* Saved versions */}
      <BrioCard>
        <div className="flex items-center justify-between mb-3">
          <h3 style={{ fontWeight: 600 }}>Saved Versions</h3>
          <BrioButton variant="primary" onClick={handleNewVersion} disabled={creating}>
            <Plus size={14} /> {creating ? 'Creating…' : 'Customise (new version)'}
          </BrioButton>
        </div>
        {versions.length === 0 ? (
          <p style={{ fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-text-muted)' }}>
            No versions yet. Click "Customise" to create an editable version based on the standard T&amp;C.
          </p>
        ) : (
          versions.map(tpl => (
            <TcVersionRow
              key={tpl.id}
              tpl={tpl}
              isApplied={appliedId === tpl.id}
              onApply={(id) => onField('tcTemplateId', id)}
              onUpdate={onUpdateTemplate}
              onDelete={onDeleteTemplate}
              onDuplicate={handleDuplicate}
              onPreview={onPreview}
              onDownload={onDownload}
            />
          ))
        )}
      </BrioCard>
    </div>
  );
}

export default ContractsPage;
